import React, { useState, useEffect, useMemo } from "react";
import {
  Users,
  Calendar,
  Clock,
  Plus,
  Upload,
  Settings,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Edit2,
  Trash2,
  Paperclip,
  FileText,
  Check,
  ArrowRight,
  UserCheck,
  DollarSign,
  Search,
  CalendarPlus,
  FileSpreadsheet,
  X,
  File as FileIcon,
  Award
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { colaboradoresService, minutesToTime, timeToMinutes } from "../services/colaboradoresService";
import { colaboradorDocumentGenerator } from "../utils/colaboradorDocumentGenerator";
import { fiscalService } from "../services/fiscalService";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from "recharts";

const getColaboradoresArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (err) {}
    return raw.split(",").map(s => s.trim()).filter(Boolean);
  }
  return [];
};

const getWorkedMinutesFromPunches = (rawPunches) => {
  const punches = getColaboradoresArray(rawPunches);
  let worked = 0;
  for (let i = 0; i < punches.length - 1; i += 2) {
    const entryVal = punches[i];
    const exitVal = punches[i + 1];
    const diff = timeToMinutes(exitVal) - timeToMinutes(entryVal);
    if (diff > 0) worked += diff;
  }
  return worked;
};

const getMinutosCompensados = (p) => {
  if (!p) return 0;
  if (p.minutos_compensados !== undefined && p.minutos_compensados !== null && Number(p.minutos_compensados) > 0) {
    return Number(p.minutos_compensados);
  }
  if (p.descricao) {
    const match = p.descricao.match(/^\[Compensa:\s*(\d+)\s*min\]/);
    if (match) {
      return Number(match[1]);
    }
    const matchHoras = p.descricao.match(/,\s*(\d+):(\d{2})\s*horas/);
    if (matchHoras) {
      return Number(matchHoras[1]) * 60 + Number(matchHoras[2]);
    }
  }
  return 0;
};

const parseCompensateHoursToMinutes = (str) => {
  if (!str) return 0;
  const cleanStr = String(str).trim();
  if (cleanStr.match(/^\d+$/)) {
    return parseInt(cleanStr, 10) * 60;
  }
  if (cleanStr.match(/^\d+:\d{2}$/)) {
    return timeToMinutes(cleanStr);
  }
  const decimalVal = parseFloat(cleanStr);
  if (!isNaN(decimalVal)) {
    return Math.round(decimalVal * 60);
  }
  return 0;
};

const formatCPF = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

const calculateINSS = (salary) => {
  if (salary <= 0) return 0;
  let inss = 0;
  const faixa1 = 1412.00;
  const faixa2 = 2666.68;
  const faixa3 = 4000.03;
  const faixa4 = 7786.02;

  if (salary <= faixa1) {
    inss = salary * 0.075;
  } else if (salary <= faixa2) {
    inss = (faixa1 * 0.075) + ((salary - faixa1) * 0.09);
  } else if (salary <= faixa3) {
    inss = (faixa1 * 0.075) + ((faixa2 - faixa1) * 0.09) + ((salary - faixa2) * 0.12);
  } else if (salary <= faixa4) {
    inss = (faixa1 * 0.075) + ((faixa2 - faixa1) * 0.09) + ((faixa3 - faixa2) * 0.12) + ((salary - faixa3) * 0.14);
  } else {
    inss = 908.86;
  }
  return Number(inss.toFixed(2));
};

const calculateIRRF = (baseIrrf) => {
  if (baseIrrf <= 2259.20) return 0;
  let irrf = 0;
  if (baseIrrf <= 2826.65) {
    irrf = (baseIrrf * 0.075) - 169.44;
  } else if (baseIrrf <= 3751.05) {
    irrf = (baseIrrf * 0.15) - 381.44;
  } else if (baseIrrf <= 4664.68) {
    irrf = (baseIrrf * 0.225) - 662.77;
  } else {
    irrf = (baseIrrf * 0.275) - 896.00;
  }
  return Number(Math.max(0, irrf).toFixed(2));
};

const renderFileList = (existingFiles, newFiles, onRemoveExisting, onRemoveNew, configRecord) => {
  return (
    <div className="space-y-2 mt-2">
      {existingFiles.map((filename) => {
        const fileUrl = configRecord ? pb.files.getURL(configRecord, filename) : "#";
        return (
          <div key={filename} className="flex items-center justify-between bg-slate-50 p-2.5 rounded-xl border border-slate-200">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs font-bold text-blue-600 hover:underline truncate"
            >
              <Paperclip size={14} className="shrink-0" />
              <span className="truncate">{filename}</span>
            </a>
            <button
              type="button"
              onClick={() => onRemoveExisting(filename)}
              className="p-1 text-slate-450 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
              title="Excluir arquivo permanentemente"
            >
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}
      {newFiles.map((file, idx) => (
        <div key={idx} className="flex items-center justify-between bg-blue-50/50 p-2.5 rounded-xl border border-blue-150">
          <div className="flex items-center gap-2 text-xs text-slate-700 font-semibold truncate">
            <Upload size={14} className="text-blue-500 shrink-0" />
            <span className="truncate">{file.name}</span>
            <span className="text-[10px] text-slate-400 font-mono">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
          <button
            type="button"
            onClick={() => onRemoveNew(idx)}
            className="p-1 text-slate-450 hover:text-slate-650 rounded-lg hover:bg-slate-100 transition-colors"
            title="Remover anexo"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      {existingFiles.length === 0 && newFiles.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nenhum arquivo anexado.</p>
      )}
    </div>
  );
};

export default function ColaboradoresTab({ user, hasAccess }) {
  const isAdmin = useMemo(() => {
    return user?.role === "master" || (typeof hasAccess === "function" && hasAccess("colaboradores"));
  }, [user, hasAccess]);

  // --- SUB-TABS STATES ---
  const [activeSubTab, setActiveSubTab] = useState(isAdmin ? "geral" : "meu_banco");
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());

  // --- DATA STATES ---
  const [users, setUsers] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [escalas, setEscalas] = useState([]);
  const [pontoRegistros, setPontoRegistros] = useState([]);
  const [pontoRegistros6Meses, setPontoRegistros6Meses] = useState([]);
  const [pontoRegistrosTodos, setPontoRegistrosTodos] = useState([]);
  const [payments, setPayments] = useState([]);
  const [saldoAcumulado6Meses, setSaldoAcumulado6Meses] = useState(0);
  const [loading, setLoading] = useState(true);

  // --- FILTERS & INTERACTION ---
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedColaboradorDetail, setSelectedColaboradorDetail] = useState(null);

  // --- FORM STATES (MODALS) ---
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [adjustmentDate, setAdjustmentDate] = useState("");
  const [adjustmentColaborador, setAdjustmentColaborador] = useState(null);
  const [adjPunches, setAdjPunches] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjAbono, setAdjAbono] = useState("0");
  const [adjJustificativaAbono, setAdjJustificativaAbono] = useState("");
  const [adjCertificadoFile, setAdjCertificadoFile] = useState(null);

  const [isEscalaModalOpen, setIsEscalaModalOpen] = useState(false);
  const [escalaEditId, setEscalaEditId] = useState(null);
  const [escalaSelectedEquipe, setEscalaSelectedEquipe] = useState("");
  const [escalaDate, setEscalaDate] = useState("");
  const [escalaUsers, setEscalaUsers] = useState([]);
  const [escalaEntrada, setEscalaEntrada] = useState("09:00");
  const [escalaSaida, setEscalaSaida] = useState("13:00");
  const [escalaObs, setEscalaObs] = useState("");

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [configUserId, setConfigUserId] = useState("");
  const [configNameMapping, setConfigNameMapping] = useState("");
  const [configAdmissao, setConfigAdmissao] = useState("");
  const [configTolerance, setConfigTolerance] = useState("10");
  const [configInitialBank, setConfigInitialBank] = useState("0");
  const [configActive, setConfigActive] = useState(true);
  const [configSalario, setConfigSalario] = useState("0");
  const [configDiaPagamento, setConfigDiaPagamento] = useState("5");
  const [configDiaVale, setConfigDiaVale] = useState("20");
  const [configContratante, setConfigContratante] = useState("");
  const [activeDocModal, setActiveDocModal] = useState(null);
  
  // --- STATES FOR FERRAMENTAS & DOCUMENTOS ---
  const [feriasDias, setFeriasDias] = useState("30");
  const [feriasDataInicio, setFeriasDataInicio] = useState(() => new Date().toISOString().substring(0, 10));
  const [feriasVenderTerco, setFeriasVenderTerco] = useState(false);
  const [feriasAdiantar13, setFeriasAdiantar13] = useState(false);
  const [feriasResult, setFeriasResult] = useState(null);

  const [rescisaoDataDemissao, setRescisaoDataDemissao] = useState(() => new Date().toISOString().substring(0, 10));
  const [rescisaoMotivo, setRescisaoMotivo] = useState("Sem Justa Causa pelo Empregador");
  const [rescisaoAvisoPrevio, setRescisaoAvisoPrevio] = useState("Indenizado");
  const [rescisaoSaldoFGTS, setRescisaoSaldoFGTS] = useState("500");
  const [rescisaoFeriasVencidas, setRescisaoFeriasVencidas] = useState(false);
  const [rescisaoResult, setRescisaoResult] = useState(null);

  const [selectedDocModel, setSelectedDocModel] = useState("solicitacao_vt");
  const [docEmissaoData, setDocEmissaoData] = useState(() => new Date().toISOString().substring(0, 10));

  // Dynamic parameters for document forms
  const [docEmpresaTransporte, setDocEmpresaTransporte] = useState("SPTrans (Bilhete Único)");
  const [docTrajetoLinhas, setDocTrajetoLinhas] = useState("Residência / Trabalho / Residência");
  const [docQuantidadeDiaria, setDocQuantidadeDiaria] = useState("2");
  const [docTarifaUnitaria, setDocTarifaUnitaria] = useState("5.00");
  const [docMeioLocomocao, setDocMeioLocomocao] = useState("veículo próprio");
  const [docDiasSuspensao, setDocDiasSuspensao] = useState("1");
  const [docDataInicioSuspensao, setDocDataInicioSuspensao] = useState(() => new Date().toISOString().substring(0, 10));
  const [docDataRetornoSuspensao, setDocDataRetornoSuspensao] = useState(() => new Date().toISOString().substring(0, 10));
  const [docDataOcorrencia, setDocDataOcorrencia] = useState(() => new Date().toISOString().substring(0, 10));
  const [docMotivoOcorrencia, setDocMotivoOcorrencia] = useState("");
  const [docPeriodoAquisitivo, setDocPeriodoAquisitivo] = useState(() => {
    const currentYear = new Date().getFullYear();
    return `${currentYear - 1}/${currentYear}`;
  });
  const [docDataInicioFerias, setDocDataInicioFerias] = useState(() => new Date().toISOString().substring(0, 10));
  const [docDataFimFerias, setDocDataFimFerias] = useState(() => new Date().toISOString().substring(0, 10));
  const [docDataRetornoFerias, setDocDataRetornoFerias] = useState(() => new Date().toISOString().substring(0, 10));
  const [docMotivoJustaCausa, setDocMotivoJustaCausa] = useState("");
  const [docArtigoCLT, setDocArtigoCLT] = useState("artigo 482, alínea 'e' (desídia)");
  const [docOpcaoReducao, setDocOpcaoReducao] = useState("dias"); // 'dias' | 'horas'
  const [docDataInicioAviso, setDocDataInicioAviso] = useState(() => new Date().toISOString().substring(0, 10));
  const [docDataFimAviso, setDocDataFimAviso] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().substring(0, 10);
  });
  const [docDataDemissao, setDocDataDemissao] = useState(() => new Date().toISOString().substring(0, 10));
  const [docDiasAvisoProporcional, setDocDiasAvisoProporcional] = useState("30");

  // Parameters for Olerite (Vale & Pagamento)
  const [docOleriteMesAno, setDocOleriteMesAno] = useState("");
  const [docOleriteSalarioBase, setDocOleriteSalarioBase] = useState("0.00");
  const [docOleriteHorasTrabalhadas, setDocOleriteHorasTrabalhadas] = useState("220");
  const [docOleriteHorasExtras, setDocOleriteHorasExtras] = useState("0");
  const [docOleriteValorHorasExtras, setDocOleriteValorHorasExtras] = useState("0.00");
  const [docOleriteValeAdiantamento, setDocOleriteValeAdiantamento] = useState("0.00");
  const [docOleriteDescontos, setDocOleriteDescontos] = useState("0.00");
  const [docOleriteValorLiquido, setDocOleriteValorLiquido] = useState("0.00");
  const [docOleriteObservacoes, setDocOleriteObservacoes] = useState("");

  // Update defaults when selectedColaboradorDetail or configs changes
  useEffect(() => {
    const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
    if (config) {
      const sal = config.salario || 0;
      setDocOleriteSalarioBase(sal.toFixed(2));
      const vale = sal * 0.4;
      setDocOleriteValeAdiantamento(vale.toFixed(2));
      
      const months = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
      const d = new Date();
      setDocOleriteMesAno(`${months[d.getMonth()]} / ${d.getFullYear()}`);
    }
  }, [selectedColaboradorDetail, configs]);

  // Recalculate net value
  useEffect(() => {
    const sal = Number(docOleriteSalarioBase) || 0;
    const extra = Number(docOleriteValorHorasExtras) || 0;
    const vale = Number(docOleriteValeAdiantamento) || 0;
    const desc = Number(docOleriteDescontos) || 0;
    
    if (selectedDocModel === "olerite_vale") {
      setDocOleriteValorLiquido((vale - desc).toFixed(2));
    } else if (selectedDocModel === "olerite_pagamento") {
      setDocOleriteValorLiquido((sal + extra - vale - desc).toFixed(2));
    }
  }, [docOleriteSalarioBase, docOleriteValorHorasExtras, docOleriteValeAdiantamento, docOleriteDescontos, selectedDocModel]);

  const [fiscalConfigs, setFiscalConfigs] = useState([]);

  const [showInactive, setShowInactive] = useState(false);
  const [jornadas, setJornadas] = useState({
    seg: { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 },
    ter: { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 },
    qua: { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 },
    qui: { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 },
    sex: { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 }
  });

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [payColaboradorId, setPayColaboradorId] = useState("");
  const [payMonth, setPayMonth] = useState(() => new Date().getMonth() + 1);
  const [payYear, setPayYear] = useState(() => new Date().getFullYear());
  const [payTipo, setPayTipo] = useState("bonus");
  const [payValor, setPayValor] = useState("");
  const [payDesc, setPayDesc] = useState("");
  const [payPago, setPayPago] = useState(false);
  const [payCompensateHours, setPayCompensateHours] = useState("");
  const [payCompensateHoursPart, setPayCompensateHoursPart] = useState(0);
  const [payCompensateMinutesPart, setPayCompensateMinutesPart] = useState(0);
  const [payIsDescontoBanco, setPayIsDescontoBanco] = useState(false);

  // --- IMPORT STATES ---
  const [importLoading, setImportLoading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [importMappings, setImportMappings] = useState({});

  // --- UNIFIED PANEL & CADASTRO STATES ---
  const [panelActiveTab, setPanelActiveTab] = useState("espelho");

  const [cadNomeCompleto, setCadNomeCompleto] = useState("");
  const [cadCpf, setCadCpf] = useState("");
  const [cadDataNascimento, setCadDataNascimento] = useState("");
  const [cadEndereco, setCadEndereco] = useState("");
  const [cadBanco, setCadBanco] = useState("");
  const [cadAgencia, setCadAgencia] = useState("");
  const [cadConta, setCadConta] = useState("");
  const [cadPixChave, setCadPixChave] = useState("");
  const [cadTamanhoCamiseta, setCadTamanhoCamiseta] = useState("");
  const [cadTamanhoCalcado, setCadTamanhoCalcado] = useState("");
  const [cadEmail, setCadEmail] = useState("");
  const [cadPhone, setCadPhone] = useState("");
  const [cadMatriculaEsocial, setCadMatriculaEsocial] = useState("");
  const [cadArquivos, setCadArquivos] = useState([]);
  const [cadArquivosExistentes, setCadArquivosExistentes] = useState([]);

  // --- CARGOS & EQUIPES STATES ---
  const [cargos, setCargos] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [cadCargo, setCadCargo] = useState("");
  const [cadEquipe, setCadEquipe] = useState("");

  const [structureActiveSubTab, setStructureActiveSubTab] = useState("cargos");
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState("");

  // Cargo modal states
  const [isCargoModalOpen, setIsCargoModalOpen] = useState(false);
  const [cargoEditId, setCargoEditId] = useState(null);
  const [cargoNome, setCargoNome] = useState("");
  const [cargoDesc, setCargoDesc] = useState("");
  const [cargoCBO, setCargoCBO] = useState("");
  const [cargoSetor, setCargoSetor] = useState("");
  const [cargoContratante, setCargoContratante] = useState("");

  // Equipe modal states
  const [isEquipeModalOpen, setIsEquipeModalOpen] = useState(false);
  const [equipeEditId, setEquipeEditId] = useState(null);
  const [equipeNome, setEquipeNome] = useState("");
  const [equipeDesc, setEquipeDesc] = useState("");
  const [equipeLider, setEquipeLider] = useState("");
  const [equipeViceLider, setEquipeViceLider] = useState("");
  const [equipeContratante, setEquipeContratante] = useState("");

  const [deslData, setDeslData] = useState("");
  const [deslCalculo, setDeslCalculo] = useState("");
  const [deslArquivos, setDeslArquivos] = useState([]);
  const [deslArquivosExistentes, setDeslArquivosExistentes] = useState([]);

  const enrichConfigWithCompany = (configObj) => {
    if (!configObj) return configObj;
    
    const companyName = configContratante || configObj.contratante || "Sempre Joias";
    let companyInfo = fiscalConfigs.find(fc => fc.razao_social === companyName);
    
    // Fallback to the first company if no match
    if (!companyInfo && fiscalConfigs.length > 0) {
      companyInfo = fiscalConfigs[0];
    }
    
    if (!companyInfo) companyInfo = {};
    
    return {
      ...configObj,
      contratante: companyInfo.razao_social || companyName,
      companyName: companyInfo.razao_social || companyName,
      companyCnpj: companyInfo.cnpj || "",
      companyPhone: companyInfo.telefone || "",
      companyAddress: companyInfo.endereco || "",
      companyBairro: companyInfo.bairro || "",
      companyCidade: companyInfo.cidade || "",
      companyCep: companyInfo.cep || "",
      companyEmail: companyInfo.email || "",
      companyLogoUrl: companyInfo.logo ? pb.files.getUrl(companyInfo, companyInfo.logo) : "",
      companyAssinaturaUrl: companyInfo.assinatura ? pb.files.getUrl(companyInfo, companyInfo.assinatura) : ""
    };
  };

  // --- LOAD ALL DATA ---
  const loadAllData = async () => {
    setLoading(true);
    try {
      // 1. Carrega Usuários do sistema
      let listUsers = [];
      try {
        listUsers = await pb.collection("users").getFullList({ sort: "name" });
      } catch (err) {
        console.warn("Pocketbase users fetch error, mapping locally.");
      }
      setUsers(listUsers);

      // 2. Carrega Configurações dos Colaboradores
      const listConfigs = await colaboradoresService.fetchConfigs();
      setConfigs(listConfigs);

      // 3. Carrega Escalas de Sábado
      const listEscalas = await colaboradoresService.fetchEscalas();
      setEscalas(listEscalas);

      // 4. Carrega todos os registros de ponto de todos os tempos (filtra por colaborador se não for admin)
      const colabId = isAdmin ? null : user?.id;
      const listPontosTodos = await colaboradoresService.fetchAllPontoRegistros(colabId);
      setPontoRegistrosTodos(listPontosTodos);

      // Filtra os registros do mês selecionado
      const pad = (n) => String(n).padStart(2, "0");
      const monthStr = `${selectedYear}-${pad(selectedMonth)}`;
      const listPontos = listPontosTodos.filter(r => r.data.startsWith(monthStr));
      setPontoRegistros(listPontos);

      // Filtra os registros dos últimos 6 meses
      const start = new Date(selectedYear, selectedMonth - 6, 1);
      const end = new Date(selectedYear, selectedMonth, 0);
      const startStr = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`;
      const endStr = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
      const listPontos6Meses = listPontosTodos.filter(r => r.data >= startStr && r.data <= endStr);
      setPontoRegistros6Meses(listPontos6Meses);

      // 5. Carrega Histórico de Pagamentos de todos os tempos (ano = 0, mes = 0)
      const listPayments = await colaboradoresService.fetchPayments(colabId, 0, 0);
      setPayments(listPayments);

      // 6. Carrega Ponto Registros dos últimos 6 meses para o colaborador logado (se não for admin)
      if (!isAdmin && user?.id) {
        const myConfig = listConfigs.find(c => c.colaborador === user.id);
        const myAdmissionDateStr = myConfig?.data_admissao ? myConfig.data_admissao.substring(0, 10) : null;

        let colabCompensations = 0;
        listPayments.forEach(p => {
          if (!p.pago) return;
          const mins = getMinutosCompensados(p);
          if (p.tipo === "banco_horas_pago") {
            colabCompensations -= mins;
          } else if (p.tipo === "desconto") {
            colabCompensations += mins;
          }
        });

        const totalMin = listPontos6Meses.reduce((acc, curr) => {
          if (myAdmissionDateStr && curr.data < myAdmissionDateStr) {
            return acc;
          }
          return acc + (curr.saldo_minutos || 0);
        }, 0) + colabCompensations;
        setSaldoAcumulado6Meses(totalMin);
      }

      // 7. Carrega Configurações Fiscais
      try {
        const listFiscal = await fiscalService.fetchConfigs();
        setFiscalConfigs(listFiscal);
      } catch (err) {
        console.warn("Erro ao carregar configurações fiscais:", err);
      }

      // 8. Carrega Cargos
      try {
        const listCargos = await colaboradoresService.fetchCargos();
        setCargos(listCargos);
      } catch (err) {
        console.warn("Erro ao carregar cargos:", err);
      }

      // 9. Carrega Equipes
      try {
        const listEquipes = await colaboradoresService.fetchEquipes();
        setEquipes(listEquipes);
      } catch (err) {
        console.warn("Erro ao carregar equipes:", err);
      }

    } catch (err) {
      console.error("Erro ao carregar dados do ponto:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [selectedMonth, selectedYear]);

  // Set default company filter when fiscal configs are loaded
  useEffect(() => {
    if (fiscalConfigs.length > 0 && !selectedCompanyFilter) {
      setSelectedCompanyFilter(fiscalConfigs[0].razao_social);
    }
  }, [fiscalConfigs, selectedCompanyFilter]);

  // Reset selected cargo/equipe if they do not belong to the selected company
  useEffect(() => {
    if (configContratante) {
      const currentCargoObj = cargos.find(c => c.id === cadCargo);
      if (currentCargoObj && currentCargoObj.contratante && currentCargoObj.contratante !== configContratante) {
        setCadCargo("");
      }
      const currentEquipeObj = equipes.find(e => e.id === cadEquipe);
      if (currentEquipeObj && currentEquipeObj.contratante && currentEquipeObj.contratante !== configContratante) {
        setCadEquipe("");
      }
    }
  }, [configContratante, cargos, equipes, cadCargo, cadEquipe]);

  // Populate Panel fields when selectedColaboradorDetail changes
  useEffect(() => {
    if (selectedColaboradorDetail) {
      const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
      const colabUser = users.find(u => u.id === selectedColaboradorDetail);
      
      // Populate Ficha Cadastral fields
      setCadNomeCompleto(config?.nome_completo || "");
      setCadCpf(config?.cpf || "");
      setCadDataNascimento(config?.data_nascimento || "");
      setCadEndereco(config?.endereco || "");
      setCadBanco(config?.banco || "");
      setCadAgencia(config?.agencia || "");
      setCadConta(config?.conta || "");
      setCadPixChave(config?.pix_chave || "");
      setCadTamanhoCamiseta(config?.tamanho_camiseta || "");
      setCadTamanhoCalcado(config?.tamanho_calcado || "");
      setCadEmail(colabUser?.email || "");
      setCadPhone(colabUser?.phone || "");
      setCadMatriculaEsocial(config?.matricula_esocial || "");
      setCadCargo(config?.cargo || "");
      setCadEquipe(config?.equipe || "");
      setCadArquivos([]);
      setCadArquivosExistentes(config?.arquivos || []);

      // Populate Desligamento fields
      setDeslData(config?.data_desligamento ? config.data_desligamento.substring(0, 10) : "");
      setDeslCalculo(config?.calculo_rescisorio || "");
      setDeslArquivos([]);
      setDeslArquivosExistentes(config?.arquivos_rescisao || []);

      // Also set the config fields (for Jornada tab)
      setConfigUserId(selectedColaboradorDetail);
      setConfigAdmissao(config?.data_admissao ? config.data_admissao.substring(0, 10) : "");
      setConfigTolerance(String(config?.tolerancia_minutos ?? 10));
      setConfigInitialBank(String(config?.banco_horas_inicial ?? 0));
      setConfigActive(config ? !!config.ativo : true);
      setConfigNameMapping(config?.relogio_nomes?.join(", ") || "");
      setConfigSalario(String(config?.salario ?? 0));
      setConfigDiaPagamento(String(config?.dia_pagamento ?? 5));
      setConfigDiaVale(String(config?.dia_vale ?? 20));
      setConfigContratante(config?.contratante || "");

      if (config) {
        const loadDay = (key) => config[key] || { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 };
        setJornadas({
          seg: loadDay("jornada_seg"),
          ter: loadDay("jornada_ter"),
          qua: loadDay("jornada_qua"),
          qui: loadDay("jornada_qui"),
          sex: loadDay("jornada_sex")
        });
      } else {
        const padDefault = { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 };
        setJornadas({ seg: padDefault, ter: padDefault, qua: padDefault, qui: padDefault, sex: padDefault });
      }
    }
  }, [selectedColaboradorDetail, configs, users]);

  // --- AUXILIAR: Mapear dia da semana ---
  const getDayOfWeekLabel = (dateStr) => {
    const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const d = new Date(dateStr + "T00:00:00");
    return days[d.getDay()];
  };

  // --- MOTOR DE CÁLCULO GERAL DO MÊS ---
  const getColaboradorMonthlyMetrics = (colaboradorId) => {
    const config = configs.find(c => c.colaborador === colaboradorId);
    const initialBalance = config ? config.banco_horas_inicial || 0 : 0;
    
    // Filtra todos os pontos deste colaborador
    const colabRecordsTodos = pontoRegistrosTodos.filter(r => r.colaborador === colaboradorId);
    
    // Filtra pontos deste colaborador no mês selecionado
    const records = colabRecordsTodos.filter(r => {
      const pad = (n) => String(n).padStart(2, "0");
      return r.data.startsWith(`${selectedYear}-${pad(selectedMonth)}`);
    });
    
    let workedMinutes = 0;
    let delayMinutes = 0;
    let extraMinutes = 0;
    let abonoMinutes = 0;
    let periodBalance = 0;

    const admissionDateStr = config?.data_admissao ? config.data_admissao.substring(0, 10) : null;
    const dismissalDateStr = config?.data_desligamento ? config.data_desligamento.substring(0, 10) : null;

    records.forEach(r => {
      if (admissionDateStr && r.data < admissionDateStr) {
        return;
      }
      if (dismissalDateStr && r.data > dismissalDateStr) {
        return;
      }
      const punches = r.batidas_ajustadas?.length ? r.batidas_ajustadas : r.batidas_originais;
      workedMinutes += getWorkedMinutesFromPunches(punches);
      delayMinutes += r.atraso_minutos || 0;
      extraMinutes += r.hora_extra_minutos || 0;
      abonoMinutes += r.abono_minutos || 0;
      periodBalance += r.saldo_minutos || 0;
    });

    // Filtra pontos dos últimos 6 meses
    const start = new Date(selectedYear, selectedMonth - 6, 1);
    const end = new Date(selectedYear, selectedMonth, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const startStr = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`;
    const endStr = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;

    const records6m = colabRecordsTodos.filter(r => r.data >= startStr && r.data <= endStr);
    let sixMonthsBalance = 0;
    records6m.forEach(r => {
      if (admissionDateStr && r.data < admissionDateStr) {
        return;
      }
      if (dismissalDateStr && r.data > dismissalDateStr) {
        return;
      }
      sixMonthsBalance += r.saldo_minutos || 0;
    });

    // Filtra todos os pontos de todos os tempos (desde a admissão)
    let allTimePointsBalance = 0;
    colabRecordsTodos.forEach(r => {
      if (admissionDateStr && r.data < admissionDateStr) {
        return;
      }
      if (dismissalDateStr && r.data > dismissalDateStr) {
        return;
      }
      allTimePointsBalance += r.saldo_minutos || 0;
    });

    let totalCompensationsAllTime = 0;
    const colabPayments = payments.filter(p => p.colaborador === colaboradorId);
    colabPayments.forEach(p => {
      if (!p.pago) return;
      const mins = getMinutosCompensados(p);
      if (p.tipo === "banco_horas_pago") {
        totalCompensationsAllTime -= mins;
      } else if (p.tipo === "desconto") {
        totalCompensationsAllTime += mins;
      }
    });

    const accumulatedTotal = initialBalance + allTimePointsBalance + totalCompensationsAllTime;

    return {
      worked: workedMinutes,
      delay: delayMinutes,
      extra: extraMinutes,
      abono: abonoMinutes,
      periodBalance,
      sixMonthsBalance,
      accumulatedTotal,
      initialBalance
    };
  };

  // --- FILTRAR COLABORADORES PARA LISTAGEM ---
  const filteredColaboradores = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return users
      .filter(u => {
        const config = configs.find(c => c.colaborador === u.id);
        const isActive = config ? config.ativo !== false : true;
        return showInactive ? true : isActive;
      })
      .filter(u => u.name.toLowerCase().includes(query) || u.username.toLowerCase().includes(query));
  }, [users, configs, searchTerm, showInactive]);

  // --- PROCESSAMENTO E VISUALIZAÇÃO DO ESPELHO INDIVIDUAL ---
  const espelhoDetalhado = useMemo(() => {
    const colabId = selectedColaboradorDetail || user?.id;
    if (!colabId) return [];

    const pad = (n) => String(n).padStart(2, "0");
    const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
    const list = [];

    // Busca registros salvos no período
    const userRecords = pontoRegistros.filter(r => r.colaborador === colabId);
    const userConfig = configs.find(c => c.colaborador === colabId);
    const userEscalas = escalas.filter(e => getColaboradoresArray(e.colaboradores).includes(colabId));

    const admissionDateStr = userConfig?.data_admissao ? userConfig.data_admissao.substring(0, 10) : null;
    const dismissalDateStr = userConfig?.data_desligamento ? userConfig.data_desligamento.substring(0, 10) : null;

    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${selectedYear}-${pad(selectedMonth)}-${pad(day)}`;
      const savedRecord = userRecords.find(r => r.data === dateStr);

      const isBeforeAdmission = admissionDateStr && dateStr < admissionDateStr;
      const isAfterDismissal = dismissalDateStr && dateStr > dismissalDateStr;
      const isNeutralDay = isBeforeAdmission || isAfterDismissal;

      // Descobre a jornada esperada
      const scale = userEscalas.find(e => e.data === dateStr);
      let expectedMin = 0;
      let labelJornada = "Folga";
      let isScale = false;

      if (isBeforeAdmission) {
        labelJornada = "Pré-admissão";
      } else if (isAfterDismissal) {
        labelJornada = "Desligado";
      } else if (scale) {
        expectedMin = timeToMinutes(scale.jornada_saida) - timeToMinutes(scale.jornada_entrada);
        labelJornada = `Escala: ${scale.jornada_entrada}-${scale.jornada_saida}`;
        isScale = true;
      } else {
        const dateObj = new Date(dateStr + "T00:00:00");
        const dayOfWeek = dateObj.getDay();
        const days = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
        const dayKey = `jornada_${days[dayOfWeek]}`;
        
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          if (userConfig && userConfig[dayKey]) {
            const j = userConfig[dayKey];
            expectedMin = j.horas_esperadas || 480;
            labelJornada = `${j.entrada1}-${j.saida1} ${j.entrada2}-${j.saida2}`;
          } else {
            expectedMin = 480;
            labelJornada = "09:00-13:00 14:00-18:00";
          }
        }
      }

      if (isNeutralDay) {
        list.push({
          date: dateStr,
          dayNum: day,
          original: [],
          adjusted: [],
          hasAdjusted: false,
          reason: "",
          trabalhado: 0,
          atraso: 0,
          extra: 0,
          abono: 0,
          justificativa_abono: "",
          certificado: null,
          saldo: 0,
          expectedMin: 0,
          labelJornada: isBeforeAdmission ? "Pré-admissão" : "Desligado",
          isScale: false,
          id: null
        });
      } else if (savedRecord) {
        list.push({
          date: dateStr,
          dayNum: day,
          original: savedRecord.batidas_originais || [],
          adjusted: savedRecord.batidas_ajustadas || [],
          hasAdjusted: (savedRecord.batidas_ajustadas || []).length > 0,
          reason: savedRecord.motivo_ajuste || "",
          trabalhado: getWorkedMinutesFromPunches(savedRecord.batidas_ajustadas?.length ? savedRecord.batidas_ajustadas : savedRecord.batidas_originais),
          atraso: savedRecord.atraso_minutos || 0,
          extra: savedRecord.hora_extra_minutos || 0,
          abono: savedRecord.abono_minutos || 0,
          justificativa_abono: savedRecord.justificativa_abono || "",
          certificado: savedRecord.certificado_medico || null,
          saldo: savedRecord.saldo_minutos || 0,
          expectedMin,
          labelJornada,
          isScale,
          id: savedRecord.id
        });
      } else {
        // Dia neutro (ou falta sem batida ainda calculada)
        // Se for dia útil com jornada esperada e data no passado, calcula falta
        const isPast = new Date(dateStr + "T23:59:59") < new Date();
        const calcs = (isPast && expectedMin > 0)
          ? { trabalhado: 0, atraso: expectedMin, extra: 0, saldo: -expectedMin }
          : { trabalhado: 0, atraso: 0, extra: 0, saldo: 0 };

        list.push({
          date: dateStr,
          dayNum: day,
          original: [],
          adjusted: [],
          hasAdjusted: false,
          reason: "",
          trabalhado: calcs.trabalhado,
          atraso: calcs.atraso,
          extra: calcs.extra,
          abono: 0,
          justificativa_abono: "",
          certificado: null,
          saldo: calcs.saldo,
          expectedMin,
          labelJornada,
          isScale,
          id: null
        });
      }
    }

    return list;
  }, [selectedColaboradorDetail, pontoRegistros, configs, escalas, selectedMonth, selectedYear, user]);

  // --- DADOS PARA O GRÁFICO DO BANCO DE HORAS (COLABORADOR) ---
  const chartData = useMemo(() => {
    const list = espelhoDetalhado;
    const colabId = selectedColaboradorDetail || user?.id;
    const config = configs.find(c => c.colaborador === colabId);
    const startBal = config ? config.banco_horas_inicial || 0 : 0;
    
    let currentTotal = startBal;
    return list.map(item => {
      currentTotal += item.saldo;
      return {
        dia: item.dayNum,
        saldoAcumulado: Number((currentTotal / 60).toFixed(2))
      };
    });
  }, [espelhoDetalhado, configs, selectedColaboradorDetail, user]);

  // --- ACTIONS: AJUSTE DE PONTO MANUAL ---
  const handleOpenAdjustment = (dayItem) => {
    const currentColab = users.find(u => u.id === (selectedColaboradorDetail || user?.id));
    setAdjustmentColaborador(currentColab);
    setAdjustmentDate(dayItem.date);
    
    const activePunches = dayItem.adjusted.length ? dayItem.adjusted : dayItem.original;
    setAdjPunches(activePunches.join(", "));
    setAdjReason(dayItem.reason || "");
    setAdjAbono(String(dayItem.abono || 0));
    setAdjJustificativaAbono(dayItem.justificativa_abono || "");
    setAdjCertificadoFile(null);
    setIsAdjustmentModalOpen(true);
  };

  const handleSaveAdjustment = async (e) => {
    e.preventDefault();
    if (!adjustmentColaborador) return;

    // Converte batidas digitadas
    const punches = adjPunches
      .split(",")
      .map(p => p.trim())
      .filter(p => p.match(/^\d{2}:\d{2}$/));

    if (punches.length > 0 && punches.length % 2 !== 0) {
      if (!window.confirm("Número ímpar de batidas. A última batida será desconsiderada no cálculo de horas. Deseja continuar?")) {
        return;
      }
    }

    if (!adjReason && (punches.length > 0 || Number(adjAbono) > 0)) {
      alert("Por favor, preencha o motivo do ajuste ou justificativa.");
      return;
    }

    setLoading(true);
    try {
      const scale = escalas.find(e => e.data === adjustmentDate && getColaboradoresArray(e.colaboradores).includes(adjustmentColaborador.id));
      const config = configs.find(c => c.colaborador === adjustmentColaborador.id) || { tolerancia_minutos: 10 };
      
      const admissionDateStr = config?.data_admissao ? config.data_admissao.substring(0, 10) : null;
      const isBeforeAdmission = admissionDateStr && adjustmentDate < admissionDateStr;

      let expectedMin = 0;
      if (isBeforeAdmission) {
        expectedMin = 0;
      } else if (scale) {
        expectedMin = timeToMinutes(scale.jornada_saida) - timeToMinutes(scale.jornada_entrada);
      } else {
        const dObj = new Date(adjustmentDate + "T00:00:00");
        const dayOfWeek = dObj.getDay();
        const days = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
        const dayKey = `jornada_${days[dayOfWeek]}`;
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          if (config && config[dayKey]) {
            expectedMin = config[dayKey].horas_esperadas || 480;
          } else {
            expectedMin = 480;
          }
        }
      }

      const jornada = expectedMin > 0 ? { horas_esperadas: expectedMin } : null;

      // Executa o motor de cálculos
      const calcs = colaboradoresService.calcularHorasTrabalho(
        punches,
        jornada,
        config.tolerancia_minutos,
        Number(adjAbono)
      );

      const savedDay = pontoRegistros.find(r => r.colaborador === adjustmentColaborador.id && r.data === adjustmentDate);
      
      const payload = {
        batidas_originais: savedDay ? savedDay.batidas_originais : punches,
        batidas_ajustadas: punches,
        motivo_ajuste: adjReason,
        atraso_minutos: isBeforeAdmission ? 0 : calcs.atraso,
        hora_extra_minutos: isBeforeAdmission ? 0 : calcs.hora_extra,
        saldo_minutos: isBeforeAdmission ? 0 : calcs.saldo,
        abono_minutos: isBeforeAdmission ? 0 : Number(adjAbono),
        justificativa_abono: isBeforeAdmission ? "" : adjJustificativaAbono,
        certificado: isBeforeAdmission ? null : adjCertificadoFile,
        compensado: false,
        isBeforeAdmission
      };

      await colaboradoresService.savePontoRegistro(adjustmentColaborador.id, adjustmentDate, payload);
      alert("Ajuste de ponto gravado com sucesso!");
      setIsAdjustmentModalOpen(false);
      loadAllData();
    } catch (err) {
      alert("Erro ao gravar ajuste: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- IMPORTAÇÃO XLS ---
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const parsed = await colaboradoresService.parseTimeClockFile(file);
      setParsedData(parsed);
      
      // Mapeia colaboradores
      const mapped = {};
      parsed.employees.forEach(emp => {
        // 1. Busca por nome cadastrado em colaboradores_config
        const conf = configs.find(c =>
          c.relogio_nomes && c.relogio_nomes.some(n => n.toLowerCase() === emp.name.toLowerCase())
        );

        if (conf) {
          mapped[emp.id] = conf.colaborador;
        } else {
          // 2. Busca por proximidade de nome na base de usuários
          const cleanEmpName = emp.name.toLowerCase().trim();
          const matchedUser = users.find(u => 
            u.name.toLowerCase().includes(cleanEmpName) || cleanEmpName.includes(u.name.toLowerCase())
          );
          if (matchedUser) {
            mapped[emp.id] = matchedUser.id;
          }
        }
      });
      setImportMappings(mapped);
    } catch (err) {
      alert("Erro ao analisar planilha: " + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!parsedData) return;
    setImportLoading(true);
    try {
      let savedCount = 0;
      
      for (const emp of parsedData.employees) {
        const userId = importMappings[emp.id];
        if (!userId) continue;

        const config = configs.find(c => c.colaborador === userId) || { tolerancia_minutos: 10 };
        const userEscalas = escalas.filter(e => getColaboradoresArray(e.colaboradores).includes(userId));
        const admissionDateStr = config?.data_admissao ? config.data_admissao.substring(0, 10) : null;

        for (const [dayStr, punches] of Object.entries(emp.daysPunches)) {
          const pad = (n) => String(n).padStart(2, "0");
          const dateStr = `${parsedData.year}-${pad(parsedData.month)}-${pad(dayStr)}`;

          const isBeforeAdmission = admissionDateStr && dateStr < admissionDateStr;
          if (isBeforeAdmission && punches.length === 0) {
            // Se for antes da admissão e não tem batidas, não registra nada
            continue;
          }

          // Descobre jornada
          const scale = userEscalas.find(e => e.data === dateStr);
          let expectedMin = 0;
          if (isBeforeAdmission) {
            expectedMin = 0;
          } else if (scale) {
            expectedMin = timeToMinutes(scale.jornada_saida) - timeToMinutes(scale.jornada_entrada);
          } else {
            const dObj = new Date(dateStr + "T00:00:00");
            const dayOfWeek = dObj.getDay();
            const days = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
            const dayKey = `jornada_${days[dayOfWeek]}`;
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
              if (config && config[dayKey]) {
                expectedMin = config[dayKey].horas_esperadas || 480;
              } else {
                expectedMin = 480;
              }
            }
          }

          const jornada = expectedMin > 0 ? { horas_esperadas: expectedMin } : null;

          // Salva usando upsert e respeitando ajustes manuais
          await colaboradoresService.savePontoRegistro(userId, dateStr, {
            batidas_originais: punches,
            isImportProcess: true,
            jornada,
            tolerancia: config.tolerancia_minutos,
            isBeforeAdmission
          });

          savedCount++;
        }
      }

      alert(`Ponto importado! ${savedCount} registros atualizados/criados.`);
      setParsedData(null);
      setImportMappings({});
      loadAllData();
    } catch (err) {
      alert("Erro na gravação final dos pontos: " + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  // --- ESCALAS DE SÁBADO ---
  const handleOpenEscala = () => {
    setEscalaEditId(null);
    setEscalaDate("");
    setEscalaUsers([]);
    setEscalaEntrada("09:00");
    setEscalaSaida("13:00");
    setEscalaObs("");
    setEscalaSelectedEquipe("");
    setIsEscalaModalOpen(true);
  };

  const handleEditEscala = (escala) => {
    setEscalaEditId(escala.id);
    setEscalaDate(escala.data || "");
    setEscalaUsers(getColaboradoresArray(escala.colaboradores));
    setEscalaEntrada(escala.jornada_entrada || "09:00");
    setEscalaSaida(escala.jornada_saida || "13:00");
    setEscalaObs(escala.observacao || "");
    setEscalaSelectedEquipe("");
    setIsEscalaModalOpen(true);
  };

  const handleSaveEscala = async (e) => {
    e.preventDefault();
    if (!escalaDate || escalaUsers.length === 0) {
      alert("Preencha a data e selecione pelo menos um colaborador.");
      return;
    }

    setLoading(true);
    try {
      await colaboradoresService.saveEscala(
        escalaDate,
        escalaUsers,
        escalaEntrada,
        escalaSaida,
        escalaObs,
        escalaEditId
      );
      alert(escalaEditId ? "Escala de sábado atualizada com sucesso!" : "Escala de sábado gravada!");
      setIsEscalaModalOpen(false);
      setEscalaEditId(null);
      loadAllData();
    } catch (err) {
      alert("Erro ao gravar escala: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEscala = async (id) => {
    if (!window.confirm("Excluir esta escala de sábado?")) return;
    setLoading(true);
    try {
      await colaboradoresService.deleteEscala(id);
      alert("Escala removida.");
      loadAllData();
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- CARGOS & EQUIPES HANDLERS ---
  const handleOpenCargo = (cargo = null) => {
    if (cargo) {
      setCargoEditId(cargo.id);
      setCargoNome(cargo.nome || "");
      setCargoDesc(cargo.descricao || "");
      setCargoCBO(cargo.cbo || "");
      setCargoSetor(cargo.setor || "");
      setCargoContratante(cargo.contratante || "");
    } else {
      setCargoEditId(null);
      setCargoNome("");
      setCargoDesc("");
      setCargoCBO("");
      setCargoSetor("");
      setCargoContratante(selectedCompanyFilter || (fiscalConfigs[0]?.razao_social || ""));
    }
    setIsCargoModalOpen(true);
  };

  const handleSaveCargo = async (e) => {
    e.preventDefault();
    if (!cargoNome) {
      alert("O nome do cargo é obrigatório.");
      return;
    }
    setLoading(true);
    try {
      await colaboradoresService.saveCargo(cargoEditId, {
        nome: cargoNome,
        descricao: cargoDesc,
        cbo: cargoCBO,
        setor: cargoSetor,
        contratante: cargoContratante
      });
      alert(cargoEditId ? "Cargo atualizado!" : "Cargo cadastrado!");
      setIsCargoModalOpen(false);
      loadAllData();
    } catch (err) {
      alert("Erro ao salvar cargo: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCargo = async (id) => {
    if (!window.confirm("Excluir este cargo?")) return;
    setLoading(true);
    try {
      await colaboradoresService.deleteCargo(id);
      alert("Cargo excluído.");
      loadAllData();
    } catch (err) {
      alert("Erro ao excluir cargo: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEquipe = (team = null) => {
    if (team) {
      setEquipeEditId(team.id);
      setEquipeNome(team.nome || "");
      setEquipeDesc(team.descricao || "");
      setEquipeLider(team.lider || "");
      setEquipeViceLider(team.vice_lider || "");
      setEquipeContratante(team.contratante || "");
    } else {
      setEquipeEditId(null);
      setEquipeNome("");
      setEquipeDesc("");
      setEquipeLider("");
      setEquipeViceLider("");
      setEquipeContratante(selectedCompanyFilter || (fiscalConfigs[0]?.razao_social || ""));
    }
    setIsEquipeModalOpen(true);
  };

  const handleSaveEquipe = async (e) => {
    e.preventDefault();
    if (!equipeNome) {
      alert("O nome da equipe é obrigatório.");
      return;
    }
    setLoading(true);
    try {
      await colaboradoresService.saveEquipe(equipeEditId, {
        nome: equipeNome,
        descricao: equipeDesc,
        lider: equipeLider || null,
        vice_lider: equipeViceLider || null,
        contratante: equipeContratante
      });
      alert(equipeEditId ? "Equipe atualizada!" : "Equipe cadastrada!");
      setIsEquipeModalOpen(false);
      loadAllData();
    } catch (err) {
      alert("Erro ao salvar equipe: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipe = async (id) => {
    if (!window.confirm("Excluir esta equipe?")) return;
    setLoading(true);
    try {
      await colaboradoresService.deleteEquipe(id);
      alert("Equipe excluída.");
      loadAllData();
    } catch (err) {
      alert("Erro ao excluir equipe: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- CONFIGURAÇÃO DE JORNADA ---
  const handleOpenColaboradorPanel = (colabId, initialTab = "espelho") => {
    setSelectedColaboradorDetail(colabId);
    setPanelActiveTab(initialTab);
    setActiveSubTab("geral");
  };

  const handleFichaFilesChange = (e) => {
    const files = Array.from(e.target.files);
    setCadArquivos(prev => [...prev, ...files]);
  };

  const handleRemoveExistingFichaFile = (filename) => {
    setCadArquivosExistentes(prev => prev.filter(f => f !== filename));
  };

  const handleRemoveNewFichaFile = (idx) => {
    setCadArquivos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeslFilesChange = (e) => {
    const files = Array.from(e.target.files);
    setDeslArquivos(prev => [...prev, ...files]);
  };

  const handleRemoveExistingDeslFile = (filename) => {
    setDeslArquivosExistentes(prev => prev.filter(f => f !== filename));
  };

  const handleRemoveNewDeslFile = (idx) => {
    setDeslArquivos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleToggleAtivo = async (colabId, config) => {
    const isCurrentlyActive = config ? config.ativo !== false : true;
    const actionWord = isCurrentlyActive ? "inativar" : "ativar";
    
    if (!window.confirm(`Tem certeza que deseja ${actionWord} este colaborador?`)) {
      return;
    }
    
    setLoading(true);
    try {
      let payload;
      if (config) {
        payload = {
          ...config,
          ativo: !isCurrentlyActive,
          cargo: typeof config.cargo === "object" && config.cargo ? config.cargo.id : config.cargo,
          equipe: typeof config.equipe === "object" && config.equipe ? config.equipe.id : config.equipe,
          arquivos_existentes: config.arquivos || [],
          arquivos_rescisao_existentes: config.arquivos_rescisao || []
        };
      } else {
        payload = {
          ativo: false,
          relogio_nomes: [],
          tolerancia_minutos: 10,
          banco_horas_inicial: 0,
          salario: 0,
          dia_pagamento: 5,
          dia_vale: 20
        };
      }
      
      await colaboradoresService.saveConfig(colabId, payload);
      alert(`Colaborador ${isCurrentlyActive ? "inativado" : "ativado"} com sucesso!`);
      await loadAllData();
    } catch (err) {
      alert(`Erro ao alterar status do colaborador: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfig = (colabId) => {
    const config = configs.find(c => c.colaborador === colabId);
    const u = users.find(x => x.id === colabId);
    
    setConfigUserId(colabId);
    setConfigAdmissao(config?.data_admissao ? config.data_admissao.substring(0, 10) : "");
    setConfigTolerance(String(config?.tolerancia_minutos ?? 10));
    setConfigInitialBank(String(config?.banco_horas_inicial ?? 0));
    setConfigActive(config ? !!config.ativo : true);
    
    const names = config?.relogio_nomes || [];
    setConfigNameMapping(names.join(", "));

    if (config) {
      const loadDay = (key) => config[key] || { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 };
      setJornadas({
        seg: loadDay("jornada_seg"),
        ter: loadDay("jornada_ter"),
        qua: loadDay("jornada_qua"),
        qui: loadDay("jornada_qui"),
        sex: loadDay("jornada_sex")
      });
    } else {
      const padDefault = { entrada1: "09:00", saida1: "13:00", entrada2: "14:00", saida2: "18:00", horas_esperadas: 480 };
      setJornadas({ seg: padDefault, ter: padDefault, qua: padDefault, qui: padDefault, sex: padDefault });
    }

    handleOpenColaboradorPanel(colabId, "ficha");
  };

  const handleOpenDocModal = (type) => {
    setActiveDocModal(type);
    const todayStr = new Date().toISOString().substring(0, 10);
    setDocModalData({
      diasSuspensao: "1",
      dataInicioSuspensao: todayStr,
      dataRetornoSuspensao: todayStr,
      dataOcorrencia: todayStr,
      motivoOcorrencia: "",
      periodoAquisitivo: `${new Date().getFullYear() - 1}/${new Date().getFullYear()}`,
      dataInicio: todayStr,
      dataFim: todayStr,
      dataRetorno: todayStr,
      meioLocomocao: "veículo próprio"
    });
  };

  const handleGenerateDocPDF = async (e) => {
    e.preventDefault();
    if (!selectedColaboradorDetail || !activeDocModal) return;
    
    const colab = users.find(u => u.id === selectedColaboradorDetail);
    const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
    
    const activeConfig = {
      ...config,
      nome_completo: cadNomeCompleto,
      cpf: cadCpf,
      endereco: cadEndereco,
      contratante: configContratante || config?.contratante || "Sempre Joias",
      data_admissao: configAdmissao ? new Date(configAdmissao).toISOString() : (config?.data_admissao || null)
    };

    const enrichedConfig = enrichConfigWithCompany(activeConfig);
    if (activeDocModal === "advertencia" || activeDocModal === "suspensao") {
      await colaboradorDocumentGenerator.generateOcorrencia(colab, enrichedConfig, activeDocModal, docModalData);
    } else if (activeDocModal === "ferias") {
      await colaboradorDocumentGenerator.generateFerias(colab, enrichedConfig, docModalData);
    } else if (activeDocModal === "desistencia_vt") {
      await colaboradorDocumentGenerator.generateDesistenciaVT(colab, enrichedConfig, docModalData);
    }
    
    setActiveDocModal(null);
  };

  const handleCalculateFerias = () => {
    const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
    const salary = config?.salario || 0;
    
    const totalDays = Number(feriasDias);
    let vacationDays = totalDays;
    let abonoDays = 0;
    
    if (feriasVenderTerco) {
      vacationDays = Math.round(totalDays * 2 / 3);
      abonoDays = Math.round(totalDays / 3);
    }
    
    const baseVacation = (salary / 30) * vacationDays;
    const oneThirdVacation = baseVacation / 3;
    const baseAbono = feriasVenderTerco ? (salary / 30) * abonoDays : 0;
    const oneThirdAbono = feriasVenderTerco ? baseAbono / 3 : 0;
    
    const advance13 = feriasAdiantar13 ? salary / 2 : 0;
    
    const taxableIncome = baseVacation + oneThirdVacation;
    const inss = calculateINSS(taxableIncome);
    const irrf = calculateIRRF(taxableIncome - inss);
    
    const grossTotal = baseVacation + oneThirdVacation + baseAbono + oneThirdAbono + advance13;
    const deductionsTotal = inss + irrf;
    const netTotal = grossTotal - deductionsTotal;
    
    setFeriasResult({
      vacationDays,
      abonoDays,
      baseVacation,
      oneThirdVacation,
      baseAbono,
      oneThirdAbono,
      advance13,
      grossTotal,
      inss,
      irrf,
      deductionsTotal,
      netTotal
    });
  };

  const handleCalculateRescisao = () => {
    const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
    if (!config?.data_admissao) {
      alert("É necessário definir a Data de Admissão do colaborador na aba Jornada & Contrato antes de simular a rescisão.");
      return;
    }
    
    const salary = config.salario || 0;
    const start = new Date(config.data_admissao);
    const end = new Date(rescisaoDataDemissao);
    
    if (end < start) {
      alert("A data de demissão não pode ser anterior à data de admissão.");
      return;
    }
    
    let years = end.getFullYear() - start.getFullYear();
    const m = end.getMonth() - start.getMonth();
    if (m < 0 || (m === 0 && end.getDate() < start.getDate())) {
      years--;
    }
    years = Math.max(0, years);
    
    const noticeDays = Math.min(90, 30 + (years * 3));
    
    const daysWorked = end.getDate();
    const saldoSalario = (salary / 30) * daysWorked;
    
    const projectedEnd = rescisaoAvisoPrevio === "Indenizado" 
      ? new Date(end.getTime() + noticeDays * 24 * 60 * 60 * 1000) 
      : end;
      
    const startYear = projectedEnd.getFullYear();
    const firstMonthToCheck = start.getFullYear() === startYear ? start.getMonth() : 0;
    const lastMonthToCheck = projectedEnd.getMonth();
    let months13 = 0;
    
    for (let monthIdx = firstMonthToCheck; monthIdx <= lastMonthToCheck; monthIdx++) {
      if (monthIdx === firstMonthToCheck && start.getFullYear() === startYear) {
        const daysInMonth = new Date(startYear, monthIdx + 1, 0).getDate();
        const workedDays = daysInMonth - start.getDate() + 1;
        if (workedDays >= 15) months13++;
      } else if (monthIdx === lastMonthToCheck) {
        const workedDays = projectedEnd.getDate();
        if (workedDays >= 15) months13++;
      } else {
        months13++;
      }
    }
    const decimoTerceiroProp = (salary / 12) * months13;
    
    let acquisitionStart = new Date(start);
    acquisitionStart.setFullYear(end.getFullYear());
    if (acquisitionStart > end) {
      acquisitionStart.setFullYear(end.getFullYear() - 1);
    }
    
    let diffMonths = (projectedEnd.getFullYear() - acquisitionStart.getFullYear()) * 12 + (projectedEnd.getMonth() - acquisitionStart.getMonth());
    const tempDate = new Date(acquisitionStart);
    tempDate.setMonth(tempDate.getMonth() + diffMonths);
    const diffDays = Math.round((projectedEnd - tempDate) / (1000 * 60 * 60 * 24));
    let vacationMonths = diffMonths;
    if (diffDays >= 15) {
      vacationMonths++;
    }
    vacationMonths = Math.min(12, vacationMonths);
    
    const feriasProp = (salary / 12) * vacationMonths;
    const feriasPropTerco = feriasProp / 3;
    
    const feriasVencidas = rescisaoFeriasVencidas ? salary : 0;
    const feriasVencidasTerco = rescisaoFeriasVencidas ? salary / 3 : 0;
    
    let avisoPrevioValue = 0;
    let avisoPrevioDescontado = 0;
    
    if (rescisaoMotivo === "Sem Justa Causa pelo Empregador") {
      if (rescisaoAvisoPrevio === "Indenizado") {
        avisoPrevioValue = (salary / 30) * noticeDays;
      }
    } else if (rescisaoMotivo === "Acordo Comum (Reforma Trabalhista)") {
      if (rescisaoAvisoPrevio === "Indenizado") {
        avisoPrevioValue = 0.5 * (salary / 30) * noticeDays;
      }
    } else if (rescisaoMotivo === "Pedido de Demissão pelo Empregado") {
      if (rescisaoAvisoPrevio === "Indenizado" || rescisaoAvisoPrevio === "Dispensado / Não Cumprido") {
        avisoPrevioDescontado = salary;
      }
    }
    
    let multaFGTSValue = 0;
    if (rescisaoMotivo === "Sem Justa Causa pelo Empregador") {
      multaFGTSValue = 0.40 * Number(rescisaoSaldoFGTS || 0);
    } else if (rescisaoMotivo === "Acordo Comum (Reforma Trabalhista)") {
      multaFGTSValue = 0.20 * Number(rescisaoSaldoFGTS || 0);
    }
    
    const inssSaldoSalario = calculateINSS(saldoSalario);
    const inss13 = calculateINSS(decimoTerceiroProp);
    
    const irrfSaldoSalario = calculateIRRF(saldoSalario - inssSaldoSalario);
    const irrf13 = calculateIRRF(decimoTerceiroProp - inss13);
    
    const credits = saldoSalario + decimoTerceiroProp + feriasProp + feriasPropTerco + feriasVencidas + feriasVencidasTerco + avisoPrevioValue;
    const debits = inssSaldoSalario + inss13 + irrfSaldoSalario + irrf13 + avisoPrevioDescontado;
    const netRescisao = credits - debits;
    
    setRescisaoResult({
      yearsWorked: years,
      noticeDays,
      saldoSalario,
      decimoTerceiroProp,
      feriasProp,
      feriasPropTerco,
      feriasVencidas,
      feriasVencidasTerco,
      avisoPrevioValue,
      avisoPrevioDescontado,
      multaFGTSValue,
      inssSaldoSalario,
      inss13,
      irrfSaldoSalario,
      irrf13,
      credits,
      debits,
      netRescisao
    });
  };

  const handleEmitDocument = async (saveInSystem) => {
    if (!selectedColaboradorDetail) return;
    
    const colabUser = users.find(u => u.id === selectedColaboradorDetail);
    const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
    
    if (!colabUser) {
      alert("Colaborador inválido.");
      return;
    }

    const activeConfig = {
      ...config,
      nome_completo: cadNomeCompleto,
      cpf: cadCpf,
      endereco: cadEndereco,
      contratante: configContratante || config?.contratante || "Sempre Joias",
      data_admissao: configAdmissao ? new Date(configAdmissao).toISOString() : (config?.data_admissao || null),
      salario: Number(configSalario) || config?.salario || 0,
      dia_pagamento: Number(configDiaPagamento) || config?.dia_pagamento || 5,
      dia_vale: Number(configDiaVale) || config?.dia_vale || 20,
      tolerancia_minutos: Number(configTolerance) || config?.tolerancia_minutos || 10,
      banco_horas_inicial: Number(configInitialBank) || config?.banco_horas_inicial || 0,
      ativo: configActive ?? config?.ativo ?? true,
      jornada_seg: jornadas.seg,
      jornada_ter: jornadas.ter,
      jornada_qua: jornadas.qua,
      jornada_qui: jornadas.qui,
      jornada_sex: jornadas.sex
    };
    
    const docData = {
      empresaTransporte: docEmpresaTransporte,
      trajetoLinhas: docTrajetoLinhas,
      quantidadeDiaria: docQuantidadeDiaria,
      tarifaUnitaria: Number(docTarifaUnitaria),
      meioLocomocao: docMeioLocomocao,
      diasSuspensao: Number(docDiasSuspensao),
      dataInicioSuspensao: docDataInicioSuspensao,
      dataRetornoSuspensao: docDataRetornoSuspensao,
      dataOcorrencia: docDataOcorrencia,
      motivoOcorrencia: docMotivoOcorrencia,
      periodoAquisitivo: docPeriodoAquisitivo,
      dataInicio: docDataInicioFerias,
      dataFim: docDataFimFerias,
      dataRetorno: docDataRetornoFerias,
      dataInicioAviso: docDataInicioAviso,
      dataFimAviso: docDataFimAviso,
      diasAvisoProporcional: Number(docDiasAvisoProporcional),
      opcaoReducao: docOpcaoReducao,
      dataDemissao: docDataDemissao,
      motivoJustaCausa: docMotivoJustaCausa,
      artigoCLT: docArtigoCLT,
      mesAno: docOleriteMesAno,
      salarioBase: Number(docOleriteSalarioBase),
      horasTrabalhadas: docOleriteHorasTrabalhadas,
      horasExtras: docOleriteHorasExtras,
      valorHorasExtras: Number(docOleriteValorHorasExtras),
      valeAdiantamento: Number(docOleriteValeAdiantamento),
      descontos: Number(docOleriteDescontos),
      valorLiquido: Number(docOleriteValorLiquido),
      observacoes: docOleriteObservacoes
    };
    
    try {
      const enrichedConfig = enrichConfigWithCompany(activeConfig);
      if (saveInSystem) {
        setLoading(true);
        const result = await colaboradorDocumentGenerator.generate(selectedDocModel, colabUser, enrichedConfig, docData, true);
        
        // Local Download
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename;
        a.click();
        URL.revokeObjectURL(url);
        
        // Save to PocketBase
        const pdfFile = new File([result.blob], result.filename, { type: "application/pdf" });
        
        const payload = {
          colaboradorName: colabUser?.name || "Colaborador",
          relogio_nomes: activeConfig?.relogio_nomes || [],
          data_admissao: activeConfig?.data_admissao || null,
          tolerancia_minutos: activeConfig?.tolerancia_minutos ?? 10,
          banco_horas_inicial: activeConfig?.banco_horas_inicial ?? 0,
          jornada_seg: activeConfig?.jornada_seg || null,
          jornada_ter: activeConfig?.jornada_ter || null,
          jornada_qua: activeConfig?.jornada_qua || null,
          jornada_qui: activeConfig?.jornada_qui || null,
          jornada_sex: activeConfig?.jornada_sex || null,
          ativo: !!activeConfig.ativo,
          data_desligamento: activeConfig?.data_desligamento || null,
          calculo_rescisorio: activeConfig?.calculo_rescisorio || "",
          salario: activeConfig?.salario ?? 0,
          dia_pagamento: activeConfig?.dia_pagamento ?? 5,
          dia_vale: activeConfig?.dia_vale ?? 20,
          contratante: activeConfig?.contratante || "",
          nome_completo: activeConfig?.nome_completo || "",
          cpf: activeConfig?.cpf || "",
          data_nascimento: activeConfig?.data_nascimento || "",
          endereco: activeConfig?.endereco || "",
          banco: activeConfig?.banco || "",
          agencia: activeConfig?.agencia || "",
          conta: activeConfig?.conta || "",
          pix_chave: activeConfig?.pix_chave || "",
          tamanho_camiseta: activeConfig?.tamanho_camiseta || "",
          tamanho_calcado: activeConfig?.tamanho_calcado || "",
          matricula_esocial: activeConfig?.matricula_esocial || "",
          cargo: activeConfig?.cargo || null,
          equipe: activeConfig?.equipe || null,
          arquivos_existentes: activeConfig?.arquivos || [],
          arquivos: [pdfFile],
          arquivos_rescisao_existentes: activeConfig?.arquivos_rescisao || []
        };
        
        await colaboradoresService.saveConfig(selectedColaboradorDetail, payload);
        alert("Documento gerado, baixado e salvo no sistema com sucesso!");
        loadAllData();
      } else {
        await colaboradorDocumentGenerator.generate(selectedDocModel, colabUser, enrichedConfig, docData, false);
        alert("Documento gerado e baixado com sucesso!");
      }
    } catch (err) {
      alert("Erro ao emitir documento: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmitDocFromCalculator = async (docType) => {
    if (!selectedColaboradorDetail) return;
    
    const colabUser = users.find(u => u.id === selectedColaboradorDetail);
    const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
    
    if (!colabUser) {
      alert("Colaborador inválido.");
      return;
    }

    const activeConfig = {
      ...config,
      nome_completo: cadNomeCompleto,
      cpf: cadCpf,
      endereco: cadEndereco,
      contratante: configContratante || config?.contratante || "Sempre Joias",
      data_admissao: configAdmissao ? new Date(configAdmissao).toISOString() : (config?.data_admissao || null),
      salario: Number(configSalario) || config?.salario || 0,
      dia_pagamento: Number(configDiaPagamento) || config?.dia_pagamento || 5,
      dia_vale: Number(configDiaVale) || config?.dia_vale || 20,
      tolerancia_minutos: Number(configTolerance) || config?.tolerancia_minutos || 10,
      banco_horas_inicial: Number(configInitialBank) || config?.banco_horas_inicial || 0,
      ativo: configActive ?? config?.ativo ?? true,
      jornada_seg: jornadas.seg,
      jornada_ter: jornadas.ter,
      jornada_qua: jornadas.qua,
      jornada_qui: jornadas.qui,
      jornada_sex: jornadas.sex
    };
    
    let docData = {};
    let saveField = "arquivos"; // "arquivos" for ferias, "arquivos_rescisao" for aviso/justa causa
    
    if (docType === "ferias") {
      const days = Number(feriasDias) || 30;
      const dStart = new Date(feriasDataInicio + "T12:00:00");
      const dEnd = new Date(dStart.getTime());
      dEnd.setDate(dStart.getDate() + days - 1);
      const dReturn = new Date(dStart.getTime());
      dReturn.setDate(dStart.getDate() + days);
      
      const year = dStart.getFullYear();
      docData = {
        periodoAquisitivo: `${year - 1}/${year}`,
        dataInicio: feriasDataInicio,
        dataFim: dEnd.toISOString().substring(0, 10),
        dataRetorno: dReturn.toISOString().substring(0, 10)
      };
      saveField = "arquivos";
    } else if (docType === "aviso_previo_trabalhado" || docType === "aviso_previo") {
      const noticeDays = rescisaoResult?.noticeDays || 30;
      const dStart = new Date(rescisaoDataDemissao + "T12:00:00");
      const dEnd = new Date(dStart.getTime());
      dEnd.setDate(dStart.getDate() + noticeDays);
      
      docData = {
        dataInicioAviso: rescisaoDataDemissao,
        dataFimAviso: dEnd.toISOString().substring(0, 10),
        diasAvisoProporcional: noticeDays,
        opcaoReducao: "dias"
      };
      saveField = "arquivos_rescisao";
    } else if (docType === "aviso_previo_indenizado") {
      const noticeDays = rescisaoResult?.noticeDays || 30;
      docData = {
        dataDemissao: rescisaoDataDemissao,
        diasAvisoProporcional: noticeDays
      };
      saveField = "arquivos_rescisao";
    } else if (docType === "comunicado_justa_causa") {
      docData = {
        dataDemissao: rescisaoDataDemissao,
        motivoJustaCausa: "Desídia no desempenho das respectivas funções, caracterizada por atrasos e faltas injustificadas",
        artigoCLT: "artigo 482, alínea 'e' da CLT"
      };
      saveField = "arquivos_rescisao";
    }
    
    try {
      setLoading(true);
      const enrichedConfig = enrichConfigWithCompany(activeConfig);
      const result = await colaboradorDocumentGenerator.generate(docType, colabUser, enrichedConfig, docData, true);
      
      // Local Download
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
      
      // Save to PocketBase
      const pdfFile = new File([result.blob], result.filename, { type: "application/pdf" });
      
      const payload = {
        colaboradorName: colabUser?.name || "Colaborador",
        relogio_nomes: activeConfig?.relogio_nomes || [],
        data_admissao: activeConfig?.data_admissao || null,
        tolerancia_minutos: activeConfig?.tolerancia_minutos ?? 10,
        banco_horas_inicial: activeConfig?.banco_horas_inicial ?? 0,
        jornada_seg: activeConfig?.jornada_seg || null,
        jornada_ter: activeConfig?.jornada_ter || null,
        jornada_qua: activeConfig?.jornada_qua || null,
        jornada_qui: activeConfig?.jornada_qui || null,
        jornada_sex: activeConfig?.jornada_sex || null,
        ativo: !!activeConfig.ativo,
        data_desligamento: activeConfig?.data_desligamento || null,
        calculo_rescisorio: activeConfig?.calculo_rescisorio || "",
        salario: activeConfig?.salario ?? 0,
        dia_pagamento: activeConfig?.dia_pagamento ?? 5,
        dia_vale: activeConfig?.dia_vale ?? 20,
        contratante: activeConfig?.contratante || "",
        nome_completo: activeConfig?.nome_completo || "",
        cpf: activeConfig?.cpf || "",
        data_nascimento: activeConfig?.data_nascimento || "",
        endereco: activeConfig?.endereco || "",
        banco: activeConfig?.banco || "",
        agencia: activeConfig?.agencia || "",
        conta: activeConfig?.conta || "",
        pix_chave: activeConfig?.pix_chave || "",
        tamanho_camiseta: activeConfig?.tamanho_camiseta || "",
        tamanho_calcado: activeConfig?.tamanho_calcado || "",
        matricula_esocial: activeConfig?.matricula_esocial || "",
        cargo: activeConfig?.cargo || null,
        equipe: activeConfig?.equipe || null,
        arquivos_existentes: activeConfig?.arquivos || [],
        arquivos_rescisao_existentes: activeConfig?.arquivos_rescisao || []
      };
      
      if (saveField === "arquivos") {
        payload.arquivos = [pdfFile];
      } else {
        payload.arquivos_rescisao = [pdfFile];
      }
      
      await colaboradoresService.saveConfig(selectedColaboradorDetail, payload);
      alert("Documento gerado, baixado e salvo no sistema com sucesso!");
      loadAllData();
    } catch (err) {
      alert("Erro ao emitir documento: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDocHistory = async (filename) => {
    if (!window.confirm(`Deseja excluir permanentemente o documento "${filename}"?`)) return;
    setLoading(true);
    try {
      const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
      const updatedFiles = (config?.arquivos || []).filter(f => f !== filename);
      const colabUser = users.find(u => u.id === selectedColaboradorDetail);
      
      const payload = {
        colaboradorName: colabUser?.name || "Colaborador",
        relogio_nomes: config?.relogio_nomes || [],
        data_admissao: config?.data_admissao || null,
        tolerancia_minutos: config?.tolerancia_minutos ?? 10,
        banco_horas_inicial: config?.banco_horas_inicial ?? 0,
        jornada_seg: config?.jornada_seg || null,
        jornada_ter: config?.jornada_ter || null,
        jornada_qua: config?.jornada_qua || null,
        jornada_qui: config?.jornada_qui || null,
        jornada_sex: config?.jornada_sex || null,
        ativo: config ? !!config.ativo : true,
        data_desligamento: config?.data_desligamento || null,
        calculo_rescisorio: config?.calculo_rescisorio || "",
        salario: config?.salario ?? 0,
        dia_pagamento: config?.dia_pagamento ?? 5,
        dia_vale: config?.dia_vale ?? 20,
        contratante: config?.contratante || "",
        nome_completo: config?.nome_completo || "",
        cpf: config?.cpf || "",
        data_nascimento: config?.data_nascimento || "",
        endereco: config?.endereco || "",
        banco: config?.banco || "",
        agencia: config?.agencia || "",
        conta: config?.conta || "",
        pix_chave: config?.pix_chave || "",
        tamanho_camiseta: config?.tamanho_camiseta || "",
        tamanho_calcado: config?.tamanho_calcado || "",
        matricula_esocial: config?.matricula_esocial || "",
        cargo: config?.cargo || null,
        equipe: config?.equipe || null,
        arquivos_existentes: updatedFiles,
        arquivos: [],
        arquivos_rescisao_existentes: config?.arquivos_rescisao || []
      };
      
      await colaboradoresService.saveConfig(selectedColaboradorDetail, payload);
      alert("Documento excluído com sucesso!");
      loadAllData();
    } catch (err) {
      alert("Erro ao excluir documento: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFicha = async (e) => {
    e.preventDefault();
    if (!selectedColaboradorDetail) return;
    
    if (cadPixChave && cadPixChave.replace(/\D/g, "").length !== 11) {
      if (!window.confirm("A chave PIX recomendada é CPF (11 dígitos). Deseja continuar mesmo assim?")) {
        return;
      }
    }

    setLoading(true);
    try {
      const colabUser = users.find(u => u.id === selectedColaboradorDetail);
      if (colabUser) {
        const userUpdate = {};
        if (colabUser.phone !== cadPhone) {
          userUpdate.phone = cadPhone;
        }

        if (Object.keys(userUpdate).length > 0) {
          await pb.collection("users").update(selectedColaboradorDetail, userUpdate);
        }
      }

      const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
      const names = configNameMapping.split(",").map(n => n.trim().toLowerCase()).filter(Boolean);
      const initialBankMins = parseCompensateHoursToMinutes(configInitialBank.replace("-", "")) * (configInitialBank.trim().startsWith("-") ? -1 : 1);

      const data = {
        colaboradorName: colabUser?.name || "Colaborador",
        relogio_nomes: names,
        data_admissao: configAdmissao ? new Date(configAdmissao).toISOString() : null,
        tolerancia_minutos: Number(configTolerance),
        banco_horas_inicial: initialBankMins,
        jornada_seg: jornadas.seg,
        jornada_ter: jornadas.ter,
        jornada_qua: jornadas.qua,
        jornada_qui: jornadas.qui,
        jornada_sex: jornadas.sex,
        ativo: configActive,
        data_desligamento: config?.data_desligamento || null,
        calculo_rescisorio: config?.calculo_rescisorio || "",
        salario: Number(configSalario),
        dia_pagamento: Number(configDiaPagamento),
        dia_vale: Number(configDiaVale),
        contratante: configContratante,
        nome_completo: cadNomeCompleto,
        cpf: cadCpf,
        data_nascimento: cadDataNascimento,
        endereco: cadEndereco,
        banco: cadBanco,
        agencia: cadAgencia,
        conta: cadConta,
        pix_chave: cadPixChave,
        tamanho_camiseta: cadTamanhoCamiseta,
        tamanho_calcado: cadTamanhoCalcado,
        matricula_esocial: cadMatriculaEsocial,
        cargo: cadCargo,
        equipe: cadEquipe,
        arquivos_existentes: cadArquivosExistentes,
        arquivos: cadArquivos,
        arquivos_rescisao_existentes: config?.arquivos_rescisao || []
      };

      await colaboradoresService.saveConfig(selectedColaboradorDetail, data);
      alert("Ficha cadastral salva com sucesso!");
      loadAllData();
    } catch (err) {
      let errMsg = err.message;
      if (err.data && Object.keys(err.data).length > 0) {
        const details = Object.entries(err.data)
          .map(([field, info]) => `${field}: ${info.message || JSON.stringify(info)}`)
          .join("\n");
        errMsg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao salvar ficha cadastral: " + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDesligamento = async (e) => {
    e.preventDefault();
    if (!selectedColaboradorDetail) return;

    setLoading(true);
    try {
      const colabUser = users.find(u => u.id === selectedColaboradorDetail);
      const config = configs.find(c => c.colaborador === selectedColaboradorDetail);
      
      const data = {
        colaboradorName: colabUser?.name || "Colaborador",
        relogio_nomes: config?.relogio_nomes || [],
        data_admissao: config?.data_admissao || null,
        tolerancia_minutos: config?.tolerancia_minutos ?? 10,
        banco_horas_inicial: config?.banco_horas_inicial ?? 0,
        jornada_seg: config?.jornada_seg || null,
        jornada_ter: config?.jornada_ter || null,
        jornada_qua: config?.jornada_qua || null,
        jornada_qui: config?.jornada_qui || null,
        jornada_sex: config?.jornada_sex || null,
        ativo: deslData ? false : (config ? !!config.ativo : true),
        salario: config?.salario ?? 0,
        dia_pagamento: config?.dia_pagamento ?? 5,
        dia_vale: config?.dia_vale ?? 20,
        contratante: config?.contratante || "",
        nome_completo: config?.nome_completo || "",
        cpf: config?.cpf || "",
        data_nascimento: config?.data_nascimento || "",
        endereco: config?.endereco || "",
        banco: config?.banco || "",
        agencia: config?.agencia || "",
        conta: config?.conta || "",
        pix_chave: config?.pix_chave || "",
        tamanho_camiseta: config?.tamanho_camiseta || "",
        tamanho_calcado: config?.tamanho_calcado || "",
        matricula_esocial: config?.matricula_esocial || "",
        cargo: config?.cargo || null,
        equipe: config?.equipe || null,
        data_desligamento: deslData ? new Date(deslData + "T12:00:00").toISOString() : null,
        calculo_rescisorio: deslCalculo,
        arquivos_existentes: config?.arquivos || [],
        arquivos_rescisao_existentes: deslArquivosExistentes,
        arquivos_rescisao: deslArquivos
      };

      await colaboradoresService.saveConfig(selectedColaboradorDetail, data);
      alert("Dados de desligamento salvos com sucesso!");
      loadAllData();
    } catch (err) {
      let errMsg = err.message;
      if (err.data && Object.keys(err.data).length > 0) {
        const details = Object.entries(err.data)
          .map(([field, info]) => `${field}: ${info.message || JSON.stringify(info)}`)
          .join("\n");
        errMsg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao salvar desligamento: " + errMsg);
    } finally {
      setLoading(false);
    }
  };

  // --- HISTÓRICO DE PAGAMENTOS ---
  const handleOpenPayment = () => {
    setPayColaboradorId("");
    setPayValor("");
    setPayDesc("");
    setPayPago(false);
    setPayCompensateHours("");
    setPayCompensateHoursPart(0);
    setPayCompensateMinutesPart(0);
    setPayIsDescontoBanco(false);
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    if (!payColaboradorId || !payValor || !payDesc) {
      alert("Por favor, preencha todos os campos.");
      return;
    }

    let mins = 0;
    let finalDesc = payDesc;
    if (payTipo === "banco_horas_pago") {
      mins = payCompensateHoursPart * 60 + payCompensateMinutesPart;
      if (mins <= 0) {
        alert("Por favor, informe uma quantidade de horas válida para compensação.");
        return;
      }
      const formatted = minutesToTime(mins);
      finalDesc = `Pagamento de saldo de banco de horas, ${formatted} horas - ${payDesc}`;
    } else if (payTipo === "desconto" && payIsDescontoBanco) {
      mins = payCompensateHoursPart * 60 + payCompensateMinutesPart;
      if (mins <= 0) {
        alert("Por favor, informe uma quantidade de horas válida para o desconto.");
        return;
      }
      const formatted = minutesToTime(mins);
      finalDesc = `Descontadas Horas devedoras do pagamento, ${formatted} horas - ${payDesc}`;
    }

    setLoading(true);
    try {
      const payload = {
        tipo: payTipo,
        valor: Number(payValor),
        descricao: finalDesc,
        pago: payPago,
        data_pagamento: payPago ? new Date().toISOString() : null,
        minutos_compensados: mins
      };

      await colaboradoresService.savePayment(payColaboradorId, payMonth, payYear, payload);
      alert("Pagamento gravado!");
      setIsPaymentModalOpen(false);
      loadAllData();
    } catch (err) {
      alert("Erro ao salvar lançamento: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm("Excluir este lançamento de pagamento/bônus?")) return;
    setLoading(true);
    try {
      await colaboradoresService.deletePayment(id);
      alert("Lançamento removido.");
      loadAllData();
    } catch (err) {
      alert("Erro ao deletar: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePaymentStatus = async (payment) => {
    setLoading(true);
    try {
      const updatedPago = !payment.pago;
      await colaboradoresService.savePayment(payment.colaborador, payment.mes, payment.ano, {
        ...payment,
        pago: updatedPago,
        minutos_compensados: getMinutosCompensados(payment)
      });
      alert(`Status do lançamento alterado para ${updatedPago ? 'Pago' : 'Pendente'}!`);
      loadAllData();
    } catch (err) {
      alert("Erro ao alterar status: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden w-full bg-slate-50">
      <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar">
        <div className="max-w-7xl mx-auto space-y-6 pb-24 px-4">
          {/* HEADER PRINCIPAL */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl border border-slate-200 shadow-sm gap-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3">
            <Users className="text-blue-600 h-7 w-7" /> Gestão de Colaboradores
          </h2>
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-1">
            Apuração de Ponto, Horas Extras, Banco de Horas e Escalas
          </p>
        </div>

        {/* Período de Apuração */}
        <div className="flex items-center gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
          <Calendar className="text-slate-400" size={18} />
          <select
            className="bg-transparent font-bold text-xs outline-none text-slate-700 cursor-pointer"
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(Number(e.target.value));
              setSelectedColaboradorDetail(null);
            }}
          >
            <option value="1">Janeiro</option>
            <option value="2">Fevereiro</option>
            <option value="3">Março</option>
            <option value="4">Abril</option>
            <option value="5">Maio</option>
            <option value="6">Junho</option>
            <option value="7">Julho</option>
            <option value="8">Agosto</option>
            <option value="9">Setembro</option>
            <option value="10">Outubro</option>
            <option value="11">Novembro</option>
            <option value="12">Dezembro</option>
          </select>
          <select
            className="bg-transparent font-bold text-xs outline-none text-slate-700 cursor-pointer"
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(Number(e.target.value));
              setSelectedColaboradorDetail(null);
            }}
          >
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
        </div>
      </div>

      {/* SUB NAV DE CONTROLES */}
      {isAdmin && (
        <div className="flex border-b border-slate-250 bg-white p-2 rounded-2xl shadow-sm gap-2 overflow-x-auto">
          <button
            onClick={() => { setActiveSubTab("geral"); setSelectedColaboradorDetail(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeSubTab === "geral" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Users size={16} /> Espelho Geral
          </button>
          <button
            onClick={() => setActiveSubTab("importacao")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeSubTab === "importacao" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Upload size={16} /> Importar Ponto
          </button>
          <button
            onClick={() => setActiveSubTab("escalas")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeSubTab === "escalas" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <CalendarPlus size={16} /> Escalas de Sábado
          </button>
          <button
            onClick={() => setActiveSubTab("configs")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeSubTab === "configs" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Settings size={16} /> Configurações
          </button>
          <button
            onClick={() => { setActiveSubTab("cargos_equipes"); setSelectedColaboradorDetail(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeSubTab === "cargos_equipes" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Users size={16} /> Cargos &amp; Equipes
          </button>
        </div>
      )}

      {/* RENDER SECTIONS */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-3">
          <Clock className="animate-spin text-blue-500" size={36} />
          <span className="font-bold text-xs text-slate-500 uppercase tracking-widest">Carregando Informações...</span>
        </div>
      ) : (
        <>
          {/* ======================================================== */}
          {/* VISÃO ADMIN: 1. ESPELHO GERAL */}
          {/* ======================================================== */}
          {isAdmin && activeSubTab === "geral" && !selectedColaboradorDetail && (
            <div className="space-y-6">
              {/* Dashboard Admin Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Users size={20} />
                  </div>
                  <div>
                    <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Colaboradores Monitorados</h4>
                    <p className="text-lg font-black text-slate-800">{filteredColaboradores.length}</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Horas Extras no Período</h4>
                    <p className="text-lg font-black text-emerald-600">
                      {minutesToTime(pontoRegistros.reduce((acc, r) => {
                        const config = configs.find(c => c.colaborador === r.colaborador);
                        const isActive = config ? config.ativo !== false : true;
                        if (!showInactive && !isActive) return acc;
                        const admissionDateStr = config?.data_admissao ? config.data_admissao.substring(0, 10) : null;
                        if (admissionDateStr && r.data < admissionDateStr) return acc;
                        return acc + (r.hora_extra_minutos || 0);
                      }, 0))}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                    <TrendingDown size={20} />
                  </div>
                  <div>
                    <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Total Atrasos no Período</h4>
                    <p className="text-lg font-black text-rose-600">
                      {minutesToTime(pontoRegistros.reduce((acc, r) => {
                        const config = configs.find(c => c.colaborador === r.colaborador);
                        const isActive = config ? config.ativo !== false : true;
                        if (!showInactive && !isActive) return acc;
                        const admissionDateStr = config?.data_admissao ? config.data_admissao.substring(0, 10) : null;
                        if (admissionDateStr && r.data < admissionDateStr) return acc;
                        return acc + (r.atraso_minutos || 0);
                      }, 0))}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                    <Award size={20} />
                  </div>
                  <div>
                    <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Horas Abonadas</h4>
                    <p className="text-lg font-black text-purple-600">
                      {minutesToTime(pontoRegistros.reduce((acc, r) => {
                        const config = configs.find(c => c.colaborador === r.colaborador);
                        const isActive = config ? config.ativo !== false : true;
                        if (!showInactive && !isActive) return acc;
                        const admissionDateStr = config?.data_admissao ? config.data_admissao.substring(0, 10) : null;
                        if (admissionDateStr && r.data < admissionDateStr) return acc;
                        return acc + (r.abono_minutos || 0);
                      }, 0))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabela de listagem */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Ficha de Apuração de Horas</h3>
                    <p className="text-slate-500 text-[11px]">Selecione um colaborador para ver o espelho detalhado do mês ou lançar ajustes.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-600 select-none">
                      <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Mostrar Inativos</span>
                    </label>
                    <div className="relative w-full sm:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        className="w-full pl-9 pr-4 py-2 border border-slate-250 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Buscar por colaborador..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Card Grid de Funcionários */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredColaboradores.map(c => {
                      const metrics = getColaboradorMonthlyMetrics(c.id);
                      const config = configs.find(cfg => cfg.colaborador === c.id);
                      const isActive = config ? config.ativo !== false : true;
                      return (
                        <div
                          key={c.id}
                          onClick={() => handleOpenColaboradorPanel(c.id, "espelho")}
                          className={`rounded-2xl border p-6 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer flex flex-col justify-between group transform hover:-translate-y-0.5 duration-200 ${
                            isActive ? "bg-white border-slate-200 shadow-sm" : "bg-slate-50 border-slate-200 opacity-75 hover:opacity-100"
                          }`}
                        >
                          <div>
                            {/* Top info */}
                            <div className="flex items-center gap-4 mb-4">
                              <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center font-black text-slate-650 border border-slate-200 overflow-hidden shrink-0">
                                {c.avatar ? (
                                  <img src={pb.files.getURL(c, c.avatar)} alt="avatar" className="h-full w-full object-cover" />
                                ) : (
                                  c.name.substring(0, 2).toUpperCase()
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenColaboradorPanel(c.id, "ficha");
                                    }}
                                    className="font-bold text-slate-800 text-sm truncate hover:text-blue-655 hover:underline transition-all"
                                  >
                                    {c.name}
                                  </h4>
                                  {!isActive && (
                                    <span className="bg-slate-200 text-slate-650 border border-slate-300 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider">
                                      Inativo
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold border shrink-0 ${
                                    metrics.accumulatedTotal >= 0
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-rose-50 text-rose-700 border-rose-250"
                                  }`}>
                                    {metrics.accumulatedTotal >= 0 ? "+" : ""}{minutesToTime(metrics.accumulatedTotal)}
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-400 font-mono">
                                  @{c.username}
                                  {config?.expand?.cargo?.nome && ` • ${config.expand.cargo.nome}`}
                                  {config?.expand?.equipe?.nome && ` (${config.expand.equipe.nome})`}
                                </p>
                              </div>
                            </div>

                            {/* Metricas do mês */}
                            <div className="grid grid-cols-4 gap-2 border-t border-slate-100 pt-4 text-center">
                              <div>
                                <span className="text-slate-400 block text-[8px] uppercase tracking-wider font-semibold">Inicial</span>
                                <span className="font-mono text-slate-700 font-bold text-xs">
                                  {minutesToTime(metrics.initialBalance)}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[8px] uppercase tracking-wider font-semibold">Este Mês</span>
                                <span className={`font-mono font-bold text-xs ${metrics.periodBalance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                                  {metrics.periodBalance >= 0 ? "+" : ""}{minutesToTime(metrics.periodBalance)}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[8px] uppercase tracking-wider font-semibold">Acum. 6m</span>
                                <span className={`font-mono font-bold text-xs ${metrics.sixMonthsBalance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                                  {metrics.sixMonthsBalance >= 0 ? "+" : ""}{minutesToTime(metrics.sixMonthsBalance)}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[8px] uppercase tracking-wider font-semibold">Total</span>
                                <span className={`font-mono font-black text-xs ${metrics.accumulatedTotal >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                                  {metrics.accumulatedTotal >= 0 ? "+" : ""}{minutesToTime(metrics.accumulatedTotal)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Bottom action button */}
                          <div className="mt-5 pt-3 border-t border-slate-50">
                            <div className="w-full py-2 bg-slate-50 group-hover:bg-blue-600 text-slate-650 group-hover:text-white rounded-xl font-bold text-[10px] uppercase tracking-wider text-center transition-all flex items-center justify-center gap-1">
                              <span>Acessar Espelho de Ponto</span>
                              <ArrowRight size={12} className="transform group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {filteredColaboradores.length === 0 && (
                    <div className="text-center py-12 text-slate-400 font-medium">
                      Nenhum colaborador com dados cadastrados para este mês.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* DETALHE ESPELHO (APENAS GESTOR/ADMIN) */}
          {/* ======================================================== */}
          {isAdmin && activeSubTab === "geral" && selectedColaboradorDetail && (
            <div className="space-y-6">
              {/* Header do Espelho Detalhado */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-5 rounded-2xl border border-slate-200 shadow-sm gap-4">
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <button
                      onClick={() => setSelectedColaboradorDetail(null)}
                      className="p-2 border border-slate-250 rounded-xl hover:bg-slate-100 transition-colors text-slate-500 font-bold"
                    >
                      Voltar
                    </button>
                  )}
                  <div>
                    <h3 className="font-black text-slate-800 text-base">
                      Painel do Colaborador: {users.find(u => u.id === selectedColaboradorDetail)?.name}
                    </h3>
                    <p className="text-xs text-slate-400 font-mono">
                      Período: {selectedMonth}/{selectedYear}
                    </p>
                  </div>
                </div>

                {/* Métricas Acumuladas */}
                <div className="flex items-center gap-4 text-xs font-bold">
                  <div>
                    <span className="text-slate-400 block text-[9px] uppercase tracking-wider">Saldo Período</span>
                    <span className={`font-mono text-sm ${
                      getColaboradorMonthlyMetrics(selectedColaboradorDetail || user?.id).periodBalance >= 0 
                        ? "text-emerald-600" : "text-rose-600"
                    }`}>
                      {minutesToTime(getColaboradorMonthlyMetrics(selectedColaboradorDetail || user?.id).periodBalance)}
                    </span>
                  </div>
                  <div className="w-[1px] h-8 bg-slate-200"></div>
                  <div>
                    <span className="text-slate-400 block text-[9px] uppercase tracking-wider">Banco de Horas Total</span>
                    <span className={`font-mono text-sm px-2.5 py-1 rounded-lg ${
                      getColaboradorMonthlyMetrics(selectedColaboradorDetail || user?.id).accumulatedTotal >= 0 
                        ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                    }`}>
                      {minutesToTime(getColaboradorMonthlyMetrics(selectedColaboradorDetail || user?.id).accumulatedTotal)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sub Nav do Painel Interno */}
              <div className="flex border-b border-slate-250 bg-white p-2 rounded-2xl shadow-sm gap-2 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setPanelActiveTab("espelho")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
                    panelActiveTab === "espelho" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-650 hover:bg-slate-100"
                  }`}
                >
                  <Clock size={16} /> Espelho de Ponto
                </button>
                <button
                  type="button"
                  onClick={() => setPanelActiveTab("ficha")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
                    panelActiveTab === "ficha" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-650 hover:bg-slate-100"
                  }`}
                >
                  <FileText size={16} /> Ficha Cadastral
                </button>
                <button
                  type="button"
                  onClick={() => setPanelActiveTab("pagamentos")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
                    panelActiveTab === "pagamentos" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-650 hover:bg-slate-100"
                  }`}
                >
                  <DollarSign size={16} /> Histórico de Pagamentos
                </button>
                <button
                  type="button"
                  onClick={() => setPanelActiveTab("desligamento")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
                    panelActiveTab === "desligamento" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-650 hover:bg-slate-100"
                  }`}
                >
                  <AlertCircle size={16} /> Desligamento
                </button>
                <button
                  type="button"
                  onClick={() => setPanelActiveTab("ferramentas_documentos")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
                    panelActiveTab === "ferramentas_documentos" ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-650 hover:bg-slate-100"
                  }`}
                >
                  <Award size={16} /> Ferramentas &amp; Documentos
                </button>
              </div>

              {/* RENDER ESPELHO DE PONTO */}
              {panelActiveTab === "espelho" && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                          <th className="px-4 py-3 text-center">Dia</th>
                          <th className="px-4 py-3">Jornada Padrão</th>
                          <th className="px-6 py-3">Batidas Originais</th>
                          <th className="px-6 py-3">Batidas Ajustadas</th>
                          <th className="px-4 py-3 text-center">Abono</th>
                          <th className="px-4 py-3 text-center">Trabalhado</th>
                          <th className="px-4 py-3 text-center">Saldo</th>
                          <th className="px-4 py-3 text-right">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {espelhoDetalhado.map(d => {
                          const dateObj = new Date(d.date + "T00:00:00");
                          const isWeekend = dateObj.getDay() === 0 || (dateObj.getDay() === 6 && !d.isScale);
                          return (
                            <tr key={d.date} className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${
                              isWeekend ? "bg-slate-50/40 text-slate-400" : "text-slate-700"
                            }`}>
                              <td className="px-4 py-3.5 text-center font-bold">
                                {d.dayNum} ({getDayOfWeekLabel(d.date)})
                              </td>
                              <td className="px-4 py-3.5 font-medium text-[10px]">
                                {d.labelJornada}
                              </td>
                              <td className="px-6 py-3.5 font-mono text-[11px] font-semibold text-slate-500">
                                {d.original.length ? d.original.join(" ➔ ") : "-"}
                              </td>
                              <td className="px-6 py-3.5 font-mono text-[11px] font-bold text-slate-800">
                                {d.adjusted.length ? (
                                  <div className="flex flex-col gap-1 items-start">
                                    <div className="flex items-center gap-1.5">
                                      <span className="bg-yellow-50 text-yellow-800 px-1.5 py-0.5 rounded text-[10px] font-bold border border-yellow-200">Ajustado</span>
                                      <span>{d.adjusted.join(" ➔ ")}</span>
                                    </div>
                                    {d.reason && (
                                      <div className="text-[10px] text-slate-500 font-normal italic leading-tight bg-slate-50 border border-slate-200 rounded px-2 py-1 max-w-[200px] whitespace-normal break-words shadow-sm mt-0.5" title={`Motivo: ${d.reason}`}>
                                        <span className="font-extrabold not-italic text-slate-650 block text-[9px] uppercase tracking-wider mb-0.5">Motivo:</span>
                                        {d.reason}
                                      </div>
                                    )}
                                  </div>
                                ) : "-"}
                              </td>
                              <td className="px-4 py-3.5 text-center font-mono font-medium">
                                {d.abono > 0 ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-lg border border-purple-100 font-bold" title={d.justificativa_abono}>
                                      +{minutesToTime(d.abono)}
                                    </span>
                                    {d.justificativa_abono && (
                                      <div className="text-[10px] text-slate-500 font-normal italic leading-tight bg-slate-50 border border-slate-200 rounded px-2 py-1 max-w-[150px] whitespace-normal break-words shadow-sm mt-0.5" title={d.justificativa_abono}>
                                        {d.justificativa_abono}
                                      </div>
                                    )}
                                  </div>
                                ) : "-"}
                              </td>
                              <td className="px-4 py-3.5 text-center font-mono font-semibold">
                                {d.trabalhado > 0 ? minutesToTime(d.trabalhado) : "-"}
                              </td>
                              <td className={`px-4 py-3.5 text-center font-mono font-bold ${
                                d.saldo > 0 ? "text-emerald-600" : d.saldo < 0 ? "text-rose-600 font-black" : "text-slate-400"
                              }`}>
                                {d.saldo > 0 ? "+" : ""}{d.saldo !== 0 ? minutesToTime(d.saldo) : "00:00"}
                              </td>
                              <td className="px-4 py-3.5 text-right">
                                <button
                                  onClick={() => handleOpenAdjustment(d)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-block"
                                  title="Ajustar batidas / abonos"
                                >
                                  <Edit2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* RENDER HISTÓRICO DE PAGAMENTOS */}
              {panelActiveTab === "pagamentos" && (
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 animate-fade-in">
                  <div className="flex justify-between items-center border-b pb-3">
                    <div>
                      <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                        <DollarSign className="text-blue-650" /> Histórico de Lançamentos
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">Pagamentos, bônus, adicionais e descontos administrativos aplicados a este colaborador.</p>
                    </div>
                    <button
                      onClick={() => {
                        setPayColaboradorId(selectedColaboradorDetail);
                        setPayMonth(selectedMonth);
                        setPayYear(selectedYear);
                        setPayValor("");
                        setPayDesc("");
                        setPayPago(false);
                        setIsPaymentModalOpen(true);
                      }}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white transition-all text-[10px] font-bold uppercase rounded-lg flex items-center gap-1 shadow-sm"
                    >
                      <Plus size={12} /> Novo Lançamento
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-150">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                          <th className="px-4 py-2">Mês/Ano</th>
                          <th className="px-4 py-2">Descrição</th>
                          <th className="px-4 py-2 text-center">Tipo</th>
                          <th className="px-4 py-2 text-center">Valor</th>
                          <th className="px-4 py-2 text-center">Status</th>
                          <th className="px-4 py-2 text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments
                          .filter(p => p.colaborador === selectedColaboradorDetail && p.mes === selectedMonth && p.ano === selectedYear)
                          .map(p => (
                            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-2.5 font-bold text-slate-800">
                                {String(p.mes).padStart(2, "0")}/{p.ano}
                              </td>
                              <td className="px-4 py-2.5 text-slate-650 font-medium">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span>{p.descricao.replace(/^\[Compensa:\s*\d+\s*min\]\s*/, "")}</span>
                                  {getMinutosCompensados(p) > 0 && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[9px] font-bold">
                                      <Clock size={10} /> {minutesToTime(getMinutosCompensados(p))}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {p.tipo === "bonus" ? (
                                  <span className="bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-lg border border-emerald-150 font-bold text-[9px]">Bônus</span>
                                ) : p.tipo === "desconto" ? (
                                  <span className="bg-rose-50 text-rose-800 px-2 py-0.5 rounded-lg border border-rose-150 font-bold text-[9px]">Desconto</span>
                                ) : p.tipo === "salario" ? (
                                  <span className="bg-blue-50 text-blue-800 px-2 py-0.5 rounded-lg border border-blue-150 font-bold text-[9px]">Salário Base</span>
                                ) : (
                                  <span className="bg-purple-50 text-purple-800 px-2 py-0.5 rounded-lg border border-purple-150 font-bold text-[9px]">Banco de Horas Pago</span>
                                )}
                              </td>
                              <td className={`px-4 py-2.5 text-center font-mono font-bold ${
                                p.tipo === "desconto" ? "text-rose-600" : "text-emerald-600"
                              }`}>
                                {p.tipo === "desconto" ? "-" : "+"} R$ {p.valor.toFixed(2)}
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleTogglePaymentStatus(p)}
                                  className={`px-2.5 py-1 rounded text-[8px] font-black uppercase border transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-sm ${
                                    p.pago
                                      ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200"
                                      : "bg-amber-100 text-amber-850 border-amber-300 hover:bg-amber-200"
                                  }`}
                                  title="Clique para alterar o status"
                                >
                                  {p.pago ? "Pago" : "Pendente"}
                                </button>
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleDeletePayment(p.id)}
                                  className="p-1 text-slate-450 hover:text-red-650 hover:bg-red-50 rounded text-slate-400"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        {payments.filter(p => p.colaborador === selectedColaboradorDetail && p.mes === selectedMonth && p.ano === selectedYear).length === 0 && (
                          <tr>
                            <td colSpan="6" className="px-4 py-8 text-center text-slate-400 font-medium">
                              Nenhum lançamento financeiro (bônus/desconto) registrado para este colaborador neste mês.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* RENDER FICHA CADASTRAL */}
              {panelActiveTab === "ficha" && (
                <form onSubmit={handleSaveFicha} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6 animate-fade-in">
                  <div className="border-b pb-3">
                    <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                      <FileText className="text-blue-600" /> Ficha Cadastral
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">Informações de cadastro do colaborador, dados para PIX/banco e documentos gerais.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome Completo</label>
                      <input
                        type="text"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        value={cadNomeCompleto}
                        onChange={(e) => setCadNomeCompleto(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">CPF</label>
                      <input
                        type="text"
                        maxLength="14"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-bold text-slate-800"
                        placeholder="000.000.000-00"
                        value={cadCpf}
                        onChange={(e) => setCadCpf(formatCPF(e.target.value))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Data de Nascimento</label>
                      <input
                        type="date"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        value={cadDataNascimento}
                        onChange={(e) => setCadDataNascimento(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">E-mail (Alterável apenas no PocketBase)</label>
                      <input
                        type="email"
                        disabled
                        className="w-full p-2.5 border border-slate-200 bg-slate-50 text-slate-400 rounded-xl text-xs outline-none cursor-not-allowed font-medium"
                        placeholder="usuario@dominio.com"
                        value={cadEmail}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Telefone</label>
                      <input
                        type="tel"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        placeholder="Ex: (11) 99999-9999"
                        value={cadPhone}
                        onChange={(e) => setCadPhone(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Matrícula eSocial</label>
                      <input
                        type="text"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        value={cadMatriculaEsocial}
                        onChange={(e) => setCadMatriculaEsocial(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Endereço Completo</label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                      placeholder="Rua, número, complemento, bairro, cidade - UF"
                      value={cadEndereco}
                      onChange={(e) => setCadEndereco(e.target.value)}
                    />
                  </div>

                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                    <h5 className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">Dados Bancários &amp; Pagamentos</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Banco</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none bg-white focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          placeholder="Ex: Itaú, Bradesco"
                          value={cadBanco}
                          onChange={(e) => setCadBanco(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Agência</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none bg-white focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          placeholder="Ex: 0123"
                          value={cadAgencia}
                          onChange={(e) => setCadAgencia(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Conta (com Dígito)</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none bg-white focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          placeholder="Ex: 12345-6"
                          value={cadConta}
                          onChange={(e) => setCadConta(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Chave PIX (CPF Recomendado)</label>
                        <input
                          type="text"
                          maxLength="14"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none bg-white focus:ring-2 focus:ring-blue-500 font-bold text-slate-800"
                          placeholder="000.000.000-00"
                          value={cadPixChave}
                          onChange={(e) => setCadPixChave(formatCPF(e.target.value))}
                        />
                        <p className="text-[9px] text-slate-400 mt-1">Insira apenas os 11 dígitos do CPF.</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Tamanho Camiseta</label>
                      <input
                        type="text"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        placeholder="Ex: M, G, GG"
                        value={cadTamanhoCamiseta}
                        onChange={(e) => setCadTamanhoCamiseta(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-555 uppercase tracking-wider mb-1">Tamanho Calçado (EPI)</label>
                      <input
                        type="text"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        placeholder="Ex: 38, 40"
                        value={cadTamanhoCalcado}
                        onChange={(e) => setCadTamanhoCalcado(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                    <h5 className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">Jornada &amp; Contrato</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nomes no Ponto (Separados por vírgula)</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          placeholder="Ex: sabrina, sabrina s"
                          value={configNameMapping}
                          onChange={(e) => setConfigNameMapping(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data de Admissão</label>
                        <input
                          type="date"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={configAdmissao}
                          onChange={(e) => setConfigAdmissao(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tolerância Diária (Minutos)</label>
                        <input
                          type="number"
                          min="0"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={configTolerance}
                          onChange={(e) => setConfigTolerance(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Banco de Horas Inicial (HH:MM/Min)</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          placeholder="Ex: -08:00 ou 120"
                          value={configInitialBank}
                          onChange={(e) => setConfigInitialBank(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Empresa Contratante</label>
                        <select
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={configContratante}
                          onChange={(e) => setConfigContratante(e.target.value)}
                        >
                          <option value="">Selecione uma empresa...</option>
                          {fiscalConfigs.map(fc => (
                            <option key={fc.id} value={fc.razao_social}>
                              {fc.razao_social} {fc.cnpj ? `(${fc.cnpj})` : ""}
                            </option>
                          ))}
                          {configContratante && !fiscalConfigs.some(fc => fc.razao_social === configContratante) && (
                            <option value={configContratante}>{configContratante}</option>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Salário Base (R$)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={configSalario}
                          onChange={(e) => setConfigSalario(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dia de Pagamento</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={configDiaPagamento}
                          onChange={(e) => setConfigDiaPagamento(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dia do Vale (Adiantamento)</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={configDiaVale}
                          onChange={(e) => setConfigDiaVale(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Cargo / Função</label>
                        <select
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={cadCargo}
                          onChange={(e) => setCadCargo(e.target.value)}
                        >
                          <option value="">Nenhum cargo selecionado...</option>
                          {cargos
                            .filter(c => c.contratante === configContratante)
                            .map(c => (
                              <option key={c.id} value={c.id}>
                                {c.nome} {c.cbo ? `(CBO: ${c.cbo})` : ""}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Equipe / Setor</label>
                        <select
                          className="w-full p-2.5 border border-slate-300 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={cadEquipe}
                          onChange={(e) => setCadEquipe(e.target.value)}
                        >
                          <option value="">Nenhuma equipe selecionada...</option>
                          {equipes
                            .filter(eq => eq.contratante === configContratante)
                            .map(eq => (
                              <option key={eq.id} value={eq.id}>
                                {eq.nome}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700 mt-2">
                        <input
                          type="checkbox"
                          checked={configActive}
                          onChange={(e) => setConfigActive(e.target.checked)}
                        />
                        <span>Colaborador Ativo (Aparecer nos relatórios)</span>
                      </label>
                    </div>

                    {/* Parametros de jornada de segunda a sexta */}
                    <div className="border-t pt-4 space-y-3">
                      <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2">Jornada de Trabalho Padrão (Seg a Sex)</h5>
                      
                      {["seg", "ter", "qua", "qui", "sex"].map(dKey => {
                        const dayNames = { seg: "Segunda-feira", ter: "Terça-feira", qua: "Quarta-feira", qui: "Quinta-feira", sex: "Sexta-feira" };
                        return (
                          <div key={dKey} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 text-xs font-bold text-slate-600">
                            <span className="w-24 text-slate-700 font-black">{dayNames[dKey]}</span>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] text-slate-400">Turno 1:</span>
                              <input
                                type="text"
                                className="w-14 p-1.5 border border-slate-350 rounded-lg text-center font-mono text-[10px] outline-none bg-slate-50 font-semibold"
                                value={jornadas[dKey].entrada1}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setJornadas(prev => ({ ...prev, [dKey]: { ...prev[dKey], entrada1: val } }));
                                }}
                              />
                              <span className="text-slate-400">às</span>
                              <input
                                type="text"
                                className="w-14 p-1.5 border border-slate-350 rounded-lg text-center font-mono text-[10px] outline-none bg-slate-50 font-semibold"
                                value={jornadas[dKey].saida1}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setJornadas(prev => ({ ...prev, [dKey]: { ...prev[dKey], saida1: val } }));
                                }}
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="text-[9px] text-slate-400">Turno 2:</span>
                              <input
                                type="text"
                                className="w-14 p-1.5 border border-slate-350 rounded-lg text-center font-mono text-[10px] outline-none bg-slate-50 font-semibold"
                                value={jornadas[dKey].entrada2}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setJornadas(prev => ({ ...prev, [dKey]: { ...prev[dKey], entrada2: val } }));
                                }}
                              />
                              <span className="text-slate-400">às</span>
                              <input
                                type="text"
                                className="w-14 p-1.5 border border-slate-350 rounded-lg text-center font-mono text-[10px] outline-none bg-slate-50 font-semibold"
                                value={jornadas[dKey].saida2}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setJornadas(prev => ({ ...prev, [dKey]: { ...prev[dKey], saida2: val } }));
                                }}
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="text-[9px] text-slate-400">Esperado (min):</span>
                              <input
                                type="number"
                                className="w-16 p-1.5 border border-slate-350 rounded-lg text-center font-mono text-[10px] outline-none bg-slate-50 font-bold"
                                value={jornadas[dKey].horas_esperadas}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setJornadas(prev => ({ ...prev, [dKey]: { ...prev[dKey], horas_esperadas: val } }));
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h5 className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">Documentos e Arquivos do Colaborador</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-2">Anexar Novo Arquivo (Prints, fotos, admissional, etc.)</label>
                        <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-6 text-center flex flex-col items-center justify-center bg-slate-50/50 cursor-pointer relative transition-all">
                          <Upload className="text-slate-400 h-8 w-8 mb-2" />
                          <span className="font-bold text-[10px] text-slate-700 uppercase tracking-wider">Escolher Arquivos</span>
                          <span className="text-[9px] text-slate-400 mt-0.5">Clique ou arraste</span>
                          <input
                            type="file"
                            multiple
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={handleFichaFilesChange}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-2">Lista de Arquivos Anexados</label>
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-2xl p-4 bg-slate-50/50">
                          {renderFileList(
                            cadArquivosExistentes,
                            cadArquivos,
                            handleRemoveExistingFichaFile,
                            handleRemoveNewFichaFile,
                            configs.find(c => c.colaborador === selectedColaboradorDetail)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 shadow-md shadow-blue-100 flex items-center gap-1.5"
                    >
                      <Check size={16} /> Salvar Ficha Cadastral
                    </button>
                  </div>
                </form>
              )}

              {/* RENDER DESLIGAMENTO */}
              {panelActiveTab === "desligamento" && (
                <form onSubmit={handleSaveDesligamento} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6 animate-fade-in">
                  <div className="border-b pb-3">
                    <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                      <AlertCircle className="text-red-500" /> Desligamento de Colaborador
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">Encerramento de contrato, registro da data de desligamento, resumo rescisório e documentos relacionados.</p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3 text-xs text-amber-800 font-semibold">
                    <AlertCircle size={20} className="shrink-0 text-amber-600" />
                    <div>
                      <p className="font-bold">Regras Importantes de Desligamento:</p>
                      <ul className="list-disc list-inside mt-1 font-medium space-y-1">
                        <li>Ao definir uma data de desligamento, o colaborador será desativado automaticamente do sistema.</li>
                        <li>A apuração de banco de horas e o cálculo de faltas serão encerrados no dia seguinte à data informada.</li>
                        <li>Qualquer dia após a data de desligamento será marcado como "Desligado", sem expectativa de jornada ou alteração de saldo.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Data de Desligamento (Último dia trabalhado)</label>
                      <input
                        type="date"
                        className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        value={deslData}
                        onChange={(e) => setDeslData(e.target.value)}
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Deixe em branco se o colaborador continuar ativo.</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Cálculo Rescisório / Observações do Encerramento</label>
                    <textarea
                      className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 h-28 resize-none font-medium"
                      placeholder="Descreva detalhes dos valores rescisórios, multas de FGTS, saldo pago, aviso prévio indenizado/trabalhado, ou qualquer outra observação importante."
                      value={deslCalculo}
                      onChange={(e) => setDeslCalculo(e.target.value)}
                    />
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h5 className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">Documentos de Rescisão / Termos de Homologação</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-2">Anexar Novo Documento (TRCT, GRRF, exame demissional, etc.)</label>
                        <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-6 text-center flex flex-col items-center justify-center bg-slate-50/50 cursor-pointer relative transition-all">
                          <Upload className="text-slate-400 h-8 w-8 mb-2" />
                          <span className="font-bold text-[10px] text-slate-700 uppercase tracking-wider">Escolher Arquivos</span>
                          <span className="text-[9px] text-slate-400 mt-0.5">Clique ou arraste</span>
                          <input
                            type="file"
                            multiple
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={handleDeslFilesChange}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-2">Anexos de Rescisão</label>
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-2xl p-4 bg-slate-50/50">
                          {renderFileList(
                            deslArquivosExistentes,
                            deslArquivos,
                            handleRemoveExistingDeslFile,
                            handleRemoveNewDeslFile,
                            configs.find(c => c.colaborador === selectedColaboradorDetail)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-red-700 shadow-md shadow-red-100 flex items-center gap-1.5"
                    >
                      <Check size={16} /> Salvar Dados de Desligamento
                    </button>
                  </div>
                </form>
              )}

              {panelActiveTab === "ferramentas_documentos" && (
                <div className="space-y-6 animate-fade-in text-left">
                  {/* Grid das Calculadoras */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Calculadora de Férias */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 border-b pb-3">
                        <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                          <Calendar size={20} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-800 text-sm">Calculadora de Férias (CLT)</h4>
                          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Simulador de Gozo e Abono Pecuniário</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dias de Férias</label>
                          <select
                            className="w-full p-2.5 border border-slate-350 rounded-xl text-xs bg-white font-semibold outline-none"
                            value={feriasDias}
                            onChange={(e) => setFeriasDias(e.target.value)}
                          >
                            <option value="30">30 dias</option>
                            <option value="20">20 dias</option>
                            <option value="15">15 dias</option>
                            <option value="10">10 dias</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data de Início</label>
                          <input
                            type="date"
                            className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-semibold outline-none"
                            value={feriasDataInicio}
                            onChange={(e) => setFeriasDataInicio(e.target.value)}
                          />
                        </div>

                        <div className="flex flex-col gap-2 pt-2">
                          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 select-none">
                            <input
                              type="checkbox"
                              checked={feriasVenderTerco}
                              onChange={(e) => setFeriasVenderTerco(e.target.checked)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>Vender 1/3 (Abono Pecuniário)</span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 select-none">
                            <input
                              type="checkbox"
                              checked={feriasAdiantar13}
                              onChange={(e) => setFeriasAdiantar13(e.target.checked)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>Adiantar 1ª Parcela do 13º Salário</span>
                          </label>
                        </div>

                        <button
                          type="button"
                          onClick={handleCalculateFerias}
                          className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider shadow-md shadow-emerald-100 transition-all animate-pulse-slow"
                        >
                          Simular Férias
                        </button>
                      </div>

                      {/* Vacation Result */}
                      {feriasResult && (
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs text-slate-700 animate-fade-in">
                          <h5 className="font-extrabold text-[11px] text-slate-800 uppercase tracking-wider border-b pb-1.5 mb-2 flex justify-between">
                            <span>Resultado Férias</span>
                            <span className="text-[10px] text-slate-450 normal-case font-medium">Salário: {formatCurrency(configs.find(c => c.colaborador === selectedColaboradorDetail)?.salario || 0)}</span>
                          </h5>
                          <div className="flex justify-between">
                            <span>Dias de Gozo:</span>
                            <span className="font-bold">{feriasResult.vacationDays} dias</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Dias Vendidos (Abono):</span>
                            <span className="font-bold">{feriasResult.abonoDays} dias</span>
                          </div>
                          <div className="flex justify-between border-t pt-1.5">
                            <span>Valor Férias:</span>
                            <span className="font-bold">{formatCurrency(feriasResult.baseVacation)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Adicional 1/3 Férias:</span>
                            <span className="font-bold">{formatCurrency(feriasResult.oneThirdVacation)}</span>
                          </div>
                          {feriasResult.abonoDays > 0 && (
                            <>
                              <div className="flex justify-between">
                                <span>Abono Pecuniário:</span>
                                <span className="font-bold">{formatCurrency(feriasResult.baseAbono)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Adicional 1/3 Abono:</span>
                                <span className="font-bold">{formatCurrency(feriasResult.oneThirdAbono)}</span>
                              </div>
                            </>
                          )}
                          {feriasResult.advance13 > 0 && (
                            <div className="flex justify-between text-blue-600">
                              <span>Adiantamento 13º:</span>
                              <span className="font-bold">{formatCurrency(feriasResult.advance13)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-red-650 border-t pt-1.5">
                            <span>(-) INSS Férias:</span>
                            <span className="font-mono font-bold">-{formatCurrency(feriasResult.inss)}</span>
                          </div>
                          {feriasResult.irrf > 0 && (
                            <div className="flex justify-between text-red-650">
                              <span>(-) IRRF Férias:</span>
                              <span className="font-mono font-bold">-{formatCurrency(feriasResult.irrf)}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t pt-2 font-black text-slate-800 text-xs">
                            <span>Total Bruto:</span>
                            <span>{formatCurrency(feriasResult.grossTotal)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2 font-black text-emerald-700 text-sm bg-emerald-50 p-2 rounded">
                            <span>Líquido a Receber:</span>
                            <span>{formatCurrency(feriasResult.netTotal)}</span>
                          </div>

                          <button
                            onClick={() => handleEmitDocFromCalculator("ferias")}
                            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center gap-2 mt-3"
                          >
                            <FileText size={14} />
                            Emitir Aviso de Férias (PDF)
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Calculadora de Rescisão */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 border-b pb-3">
                        <div className="p-2 bg-rose-50 text-rose-600 rounded-xl">
                          <AlertCircle size={20} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-800 text-sm">Calculadora de Rescisão (CLT)</h4>
                          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Simulador Rescisório (Lei 12.506/2011)</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Data de Demissão</label>
                            <input
                              type="date"
                              className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-semibold outline-none"
                              value={rescisaoDataDemissao}
                              onChange={(e) => setRescisaoDataDemissao(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Aviso Prévio</label>
                            <select
                              className="w-full p-2.5 border border-slate-355 rounded-xl text-xs bg-white font-semibold outline-none"
                              value={rescisaoAvisoPrevio}
                              onChange={(e) => setRescisaoAvisoPrevio(e.target.value)}
                            >
                              <option value="Indenizado">Indenizado</option>
                              <option value="Trabalhado">Trabalhado</option>
                              <option value="Dispensado / Não Cumprido">Dispensado / Não Cumprido</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Motivo do Desligamento</label>
                          <select
                            className="w-full p-2.5 border border-slate-350 rounded-xl text-xs bg-white font-semibold outline-none"
                            value={rescisaoMotivo}
                            onChange={(e) => setRescisaoMotivo(e.target.value)}
                          >
                            <option value="Sem Justa Causa pelo Empregador">Sem Justa Causa pelo Empregador</option>
                            <option value="Pedido de Demissão pelo Empregado">Pedido de Demissão pelo Empregado</option>
                            <option value="Com Justa Causa pelo Empregador">Com Justa Causa pelo Empregador</option>
                            <option value="Acordo Comum (Reforma Trabalhista)">Acordo Comum (Reforma Trabalhista)</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-3 items-center">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Saldo do FGTS (R$)</label>
                            <input
                              type="number"
                              min="0"
                              className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-mono font-bold outline-none"
                              value={rescisaoSaldoFGTS}
                              onChange={(e) => setRescisaoSaldoFGTS(e.target.value)}
                            />
                          </div>
                          <div className="pt-4">
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 select-none">
                              <input
                                type="checkbox"
                                checked={rescisaoFeriasVencidas}
                                onChange={(e) => setRescisaoFeriasVencidas(e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span>Férias Vencidas Devidas</span>
                            </label>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleCalculateRescisao}
                          className="w-full py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider shadow-md shadow-rose-100 transition-all"
                        >
                          Simular Rescisão
                        </button>
                      </div>

                      {/* Rescisao Result */}
                      {rescisaoResult && (
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs text-slate-700 animate-fade-in max-h-96 overflow-y-auto">
                          <h5 className="font-extrabold text-[11px] text-slate-800 uppercase tracking-wider border-b pb-1.5 mb-2 flex justify-between">
                            <span>Resultado Rescisão</span>
                            <span className="text-[10px] text-slate-450 normal-case font-medium">Anos: {rescisaoResult.yearsWorked} | Aviso: {rescisaoResult.noticeDays} dias</span>
                          </h5>
                          <div className="flex justify-between">
                            <span>Saldo de Salário ({new Date(rescisaoDataDemissao).getDate()} dias):</span>
                            <span className="font-bold">{formatCurrency(rescisaoResult.saldoSalario)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>13º Salário Proporcional:</span>
                            <span className="font-bold">{formatCurrency(rescisaoResult.decimoTerceiroProp)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Férias Proporcionais:</span>
                            <span className="font-bold">{formatCurrency(rescisaoResult.feriasProp)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>1/3 Férias Proporcionais:</span>
                            <span className="font-bold">{formatCurrency(rescisaoResult.feriasPropTerco)}</span>
                          </div>
                          {rescisaoResult.feriasVencidas > 0 && (
                            <>
                              <div className="flex justify-between">
                                <span>Férias Vencidas:</span>
                                <span className="font-bold">{formatCurrency(rescisaoResult.feriasVencidas)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>1/3 Férias Vencidas:</span>
                                <span className="font-bold">{formatCurrency(rescisaoResult.feriasVencidasTerco)}</span>
                              </div>
                            </>
                          )}
                          {rescisaoResult.avisoPrevioValue > 0 && (
                            <div className="flex justify-between text-blue-600 font-semibold">
                              <span>Aviso Prévio Indenizado:</span>
                              <span>{formatCurrency(rescisaoResult.avisoPrevioValue)}</span>
                            </div>
                          )}
                          {rescisaoResult.multaFGTSValue > 0 && (
                            <div className="flex justify-between text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded">
                              <span>Multa FGTS CEF ({rescisaoMotivo.includes("Sem Justa Causa") ? "40%" : "20%"}):</span>
                              <span>{formatCurrency(rescisaoResult.multaFGTSValue)}</span>
                            </div>
                          )}
                          {rescisaoResult.avisoPrevioDescontado > 0 && (
                            <div className="flex justify-between text-red-650">
                              <span>(-) Desconto Aviso Prévio:</span>
                              <span className="font-mono font-bold">-{formatCurrency(rescisaoResult.avisoPrevioDescontado)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-red-650 border-t pt-1.5">
                            <span>(-) INSS Saldo Salário:</span>
                            <span className="font-mono font-bold">-{formatCurrency(rescisaoResult.inssSaldoSalario)}</span>
                          </div>
                          <div className="flex justify-between text-red-650">
                            <span>(-) INSS 13º Proporcional:</span>
                            <span className="font-mono font-bold">-{formatCurrency(rescisaoResult.inss13)}</span>
                          </div>
                          {rescisaoResult.irrfSaldoSalario > 0 && (
                            <div className="flex justify-between text-red-650">
                              <span>(-) IRRF Saldo Salário:</span>
                              <span className="font-mono font-bold">-{formatCurrency(rescisaoResult.irrfSaldoSalario)}</span>
                            </div>
                          )}
                          {rescisaoResult.irrf13 > 0 && (
                            <div className="flex justify-between text-red-650">
                              <span>(-) IRRF 13º Proporcional:</span>
                              <span className="font-mono font-bold">-{formatCurrency(rescisaoResult.irrf13)}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t pt-2 font-black text-slate-800 text-xs">
                            <span>Total Proventos:</span>
                            <span className="text-emerald-700">{formatCurrency(rescisaoResult.credits)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span>Total Descontos:</span>
                            <span className="text-red-650">{formatCurrency(rescisaoResult.debits)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2 font-black text-emerald-700 text-sm bg-emerald-50 p-2 rounded">
                            <span>Líquido a Receber (Verbas):</span>
                            <span>{formatCurrency(rescisaoResult.netRescisao)}</span>
                          </div>

                          {rescisaoMotivo.includes("Justa Causa") ? (
                            <button
                              onClick={() => handleEmitDocFromCalculator("comunicado_justa_causa")}
                              className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center gap-2 mt-3"
                            >
                              <FileText size={14} />
                              Emitir Comunicado Justa Causa (PDF)
                            </button>
                          ) : (
                            <>
                              {rescisaoAvisoPrevio === "Indenizado" && (
                                <button
                                  onClick={() => handleEmitDocFromCalculator("aviso_previo_indenizado")}
                                  className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center gap-2 mt-3"
                                >
                                  <FileText size={14} />
                                  Emitir Aviso Prévio Indenizado (PDF)
                                </button>
                              )}
                              {rescisaoAvisoPrevio === "Trabalhado" && (
                                <button
                                  onClick={() => handleEmitDocFromCalculator("aviso_previo_trabalhado")}
                                  className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm hover:shadow active:scale-95 flex items-center justify-center gap-2 mt-3"
                                >
                                  <FileText size={14} />
                                  Emitir Aviso Prévio Trabalhado (PDF)
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Emissor de Documentos Corporativos */}
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-center gap-3 border-b pb-3">
                      <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                        <FileText size={20} />
                      </div>
                      <div>
                        <h4 className="font-black text-slate-800 text-sm">Emissor de Documentos Corporativos (CLT)</h4>
                        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Gerador de Termos de Pessoal e Contratos Oficiais</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Modelo de Documento</label>
                        <select
                          className="w-full p-2.5 border border-slate-350 rounded-xl text-xs bg-white font-semibold outline-none"
                          value={selectedDocModel}
                          onChange={(e) => setSelectedDocModel(e.target.value)}
                        >
                          <option value="solicitacao_vt">Solicitação de Vale-Transporte (VT)</option>
                          <option value="desistencia_vt">Opção de Desistência de VT</option>
                          <option value="aviso_previo">Aviso Prévio Trabalhado</option>
                          <option value="aviso_previo_indenizado">Aviso Prévio Indenizado</option>
                          <option value="comunicado_justa_causa">Comunicado de Justa Causa</option>
                          <option value="advertencia">Termo de Advertência Disciplinar</option>
                          <option value="suspensao">Termo de Suspensão Disciplinar</option>
                          <option value="ferias">Aviso de Concessão de Férias</option>
                          <option value="ficha_registro">Ficha de Registro Geral</option>
                          <option value="quadro_horario">Quadro de Horário Individual</option>
                          <option value="olerite_vale">Resumo de Adiantamento Salarial (Vale)</option>
                          <option value="olerite_pagamento">Resumo de Pagamento Mensal</option>
                        </select>
                      </div>

                      {/* Parâmetros dinâmicos do formulário */}
                      <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4 text-left">
                        <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b pb-1.5 mb-2">Parâmetros do Documento</h5>
                        
                        {/* 1. VT Options */}
                        {selectedDocModel === "solicitacao_vt" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Empresa de Transporte</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docEmpresaTransporte}
                                onChange={(e) => setDocEmpresaTransporte(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Trajeto / Linhas</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docTrajetoLinhas}
                                onChange={(e) => setDocTrajetoLinhas(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Quantidade Diária</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docQuantidadeDiaria}
                                onChange={(e) => setDocQuantidadeDiaria(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Tarifa Unitária (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docTarifaUnitaria}
                                onChange={(e) => setDocTarifaUnitaria(e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* 2. Desistência VT Options */}
                        {selectedDocModel === "desistencia_vt" && (
                          <div>
                            <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Meio de Locomoção Próprio</label>
                            <input
                              type="text"
                              className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                              placeholder="Ex: veículo próprio (carro/moto), carona"
                              value={docMeioLocomocao}
                              onChange={(e) => setDocMeioLocomocao(e.target.value)}
                            />
                          </div>
                        )}

                        {/* 3. Aviso Prévio Trabalhado Options */}
                        {selectedDocModel === "aviso_previo" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Início do Aviso</label>
                              <input
                                type="date"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docDataInicioAviso}
                                onChange={(e) => setDocDataInicioAviso(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Término do Aviso</label>
                              <input
                                type="date"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docDataFimAviso}
                                onChange={(e) => setDocDataFimAviso(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Dias de Aviso Proporcional</label>
                              <input
                                type="number"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docDiasAvisoProporcional}
                                onChange={(e) => setDocDiasAvisoProporcional(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Opção de Redução (Art. 488 CLT)</label>
                              <select
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOpcaoReducao}
                                onChange={(e) => setDocOpcaoReducao(e.target.value)}
                              >
                                <option value="dias">Redução de 7 dias ao final</option>
                                <option value="horas">Redução de 2 horas diárias</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* 4. Aviso Prévio Indenizado Options */}
                        {selectedDocModel === "aviso_previo_indenizado" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Data da Demissão</label>
                              <input
                                type="date"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docDataDemissao}
                                onChange={(e) => setDocDataDemissao(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Dias de Aviso Proporcional</label>
                              <input
                                type="number"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docDiasAvisoProporcional}
                                onChange={(e) => setDocDiasAvisoProporcional(e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* 5. Dispensa por Justa Causa Options */}
                        {selectedDocModel === "comunicado_justa_causa" && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Artigo CLT Enquadrado</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                placeholder="Ex: artigo 482, alínea 'e' (desídia)"
                                value={docArtigoCLT}
                                onChange={(e) => setDocArtigoCLT(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Motivo / Descrição Justa Causa</label>
                              <textarea
                                rows="3"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                placeholder="Descreva os atos ou o motivo grave que justificam a dispensa por justa causa..."
                                value={docMotivoJustaCausa}
                                onChange={(e) => setDocMotivoJustaCausa(e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* 6. Advertência & Suspensão Options */}
                        {(selectedDocModel === "advertencia" || selectedDocModel === "suspensao") && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Data da Ocorrência</label>
                                <input
                                  type="date"
                                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                  value={docDataOcorrencia}
                                  onChange={(e) => setDocDataOcorrencia(e.target.value)}
                                />
                              </div>
                              {selectedDocModel === "suspensao" && (
                                <div>
                                  <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Dias de Suspensão</label>
                                  <input
                                    type="number"
                                    min="1"
                                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                    value={docDiasSuspensao}
                                    onChange={(e) => setDocDiasSuspensao(e.target.value)}
                                  />
                                </div>
                              )}
                            </div>

                            {selectedDocModel === "suspensao" && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-[10px] font-bold text-slate-555 uppercase tracking-wider mb-1">Data de Início Suspensão</label>
                                  <input
                                    type="date"
                                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                    value={docDataInicioSuspensao}
                                    onChange={(e) => setDocDataInicioSuspensao(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-bold text-slate-555 uppercase tracking-wider mb-1">Data de Retorno Suspensão</label>
                                  <input
                                    type="date"
                                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                    value={docDataRetornoSuspensao}
                                    onChange={(e) => setDocDataRetornoSuspensao(e.target.value)}
                                  />
                                </div>
                              </div>
                            )}

                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Fato Ocorrido (Motivo)</label>
                              <textarea
                                rows="3"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                placeholder="Descreva os fatos detalhadamente..."
                                value={docMotivoOcorrencia}
                                onChange={(e) => setDocMotivoOcorrencia(e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* 7. Férias Options */}
                        {selectedDocModel === "ferias" && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Período Aquisitivo</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                placeholder="Ex: 2025/2026"
                                value={docPeriodoAquisitivo}
                                onChange={(e) => setDocPeriodoAquisitivo(e.target.value)}
                              />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Início Férias</label>
                                <input
                                  type="date"
                                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                  value={docDataInicioFerias}
                                  onChange={(e) => setDocDataInicioFerias(e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Término Férias</label>
                                <input
                                  type="date"
                                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                  value={docDataFimFerias}
                                  onChange={(e) => setDocDataFimFerias(e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Retorno ao Trabalho</label>
                                <input
                                  type="date"
                                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                  value={docDataRetornoFerias}
                                  onChange={(e) => setDocDataRetornoFerias(e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 8. Ficha Registro e Quadro de Horário */}
                        {(selectedDocModel === "ficha_registro" || selectedDocModel === "quadro_horario") && (
                          <p className="text-[10px] text-slate-400 font-bold italic py-2">
                            *Nota: Este modelo é montado automaticamente com base nos dados do colaborador (cadastro, banco de dados, salário e jornada) sem necessidade de parâmetros adicionais.
                          </p>
                        )}

                        {/* 9. Olerite Vale Options */}
                        {selectedDocModel === "olerite_vale" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Mês / Ano de Referência</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOleriteMesAno}
                                onChange={(e) => setDocOleriteMesAno(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Valor do Vale/Adiantamento (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docOleriteValeAdiantamento}
                                onChange={(e) => setDocOleriteValeAdiantamento(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Descontos (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docOleriteDescontos}
                                onChange={(e) => setDocOleriteDescontos(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Valor Líquido a Pagar (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-slate-100 font-mono font-bold outline-none border-slate-300"
                                value={docOleriteValorLiquido}
                                onChange={(e) => setDocOleriteValorLiquido(e.target.value)}
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Observações / Detalhes</label>
                              <textarea
                                rows="2"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOleriteObservacoes}
                                onChange={(e) => setDocOleriteObservacoes(e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* 10. Olerite Pagamento Options */}
                        {selectedDocModel === "olerite_pagamento" && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Mês / Ano de Referência</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOleriteMesAno}
                                onChange={(e) => setDocOleriteMesAno(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Salário Base (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docOleriteSalarioBase}
                                onChange={(e) => setDocOleriteSalarioBase(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Horas Trabalhadas</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOleriteHorasTrabalhadas}
                                onChange={(e) => setDocOleriteHorasTrabalhadas(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Horas Extras (Qtde)</label>
                              <input
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOleriteHorasExtras}
                                onChange={(e) => setDocOleriteHorasExtras(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Valor Horas Extras (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docOleriteValorHorasExtras}
                                onChange={(e) => setDocOleriteValorHorasExtras(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Desconto Vale Adiantado (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docOleriteValeAdiantamento}
                                onChange={(e) => setDocOleriteValeAdiantamento(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Outros Descontos (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-mono font-bold outline-none"
                                value={docOleriteDescontos}
                                onChange={(e) => setDocOleriteDescontos(e.target.value)}
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Valor Líquido a Pagar (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-slate-100 font-mono font-bold outline-none border-slate-300"
                                value={docOleriteValorLiquido}
                                onChange={(e) => setDocOleriteValorLiquido(e.target.value)}
                              />
                            </div>
                            <div className="md:col-span-3">
                              <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider mb-1">Observações / Detalhes</label>
                              <textarea
                                rows="2"
                                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white font-semibold outline-none"
                                value={docOleriteObservacoes}
                                onChange={(e) => setDocOleriteObservacoes(e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex flex-col sm:flex-row justify-end gap-3 pt-3 border-t">
                        <button
                          type="button"
                          onClick={() => handleEmitDocument(false)}
                          className="px-5 py-2.5 border border-slate-300 hover:bg-slate-50 text-slate-605 hover:bg-slate-100 font-bold text-xs uppercase tracking-wider rounded-xl transition-colors flex items-center justify-center gap-1.5"
                        >
                          <FileIcon size={16} /> Gerar &amp; Baixar PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEmitDocument(true)}
                          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider shadow-md shadow-blue-150 transition-all flex items-center justify-center gap-1.5"
                        >
                          <CheckCircle size={16} /> Emitir &amp; Salvar no Sistema
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Lista de Documentos Gerados (Histórico) */}
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <div>
                        <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                          <Paperclip className="text-blue-500" /> Histórico de Documentos Gerados
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">Arquivos gerados em PDF salvos diretamente na conta do colaborador para auditorias e consultas futuras.</p>
                      </div>
                      <span className="bg-slate-100 text-slate-600 border border-slate-250 px-2 py-0.5 rounded text-[10px] font-bold font-mono">
                        {configs.find(c => c.colaborador === selectedColaboradorDetail)?.arquivos?.length || 0} arquivos
                      </span>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                            <th className="px-6 py-3">Nome do Arquivo</th>
                            <th className="px-6 py-3">Tipo do Documento</th>
                            <th className="px-6 py-3 text-right">Ação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(configs.find(c => c.colaborador === selectedColaboradorDetail)?.arquivos || []).map((filename) => {
                            const configRecord = configs.find(c => c.colaborador === selectedColaboradorDetail);
                            const fileUrl = configRecord ? pb.files.getURL(configRecord, filename) : "#";
                            
                            let docTypeDisplay = "Anexo Cadastral";
                            const lower = filename.toLowerCase();
                            if (lower.startsWith("ficha_registro")) docTypeDisplay = "Ficha de Registro";
                            else if (lower.startsWith("quadro_horario")) docTypeDisplay = "Quadro de Horário";
                            else if (lower.startsWith("solicitacao_vt")) docTypeDisplay = "Solicitação VT";
                            else if (lower.startsWith("desistencia_vt")) docTypeDisplay = "Desistência VT";
                            else if (lower.startsWith("aviso_previo_trabalhado") || lower.startsWith("aviso_previo")) docTypeDisplay = "Aviso Prévio Trabalhado";
                            else if (lower.startsWith("aviso_previo_indenizado")) docTypeDisplay = "Aviso Prévio Indenizado";
                            else if (lower.startsWith("comunicado_justa_causa")) docTypeDisplay = "Justa Causa";
                            else if (lower.startsWith("advertencia")) docTypeDisplay = "Advertência Disciplinar";
                            else if (lower.startsWith("suspensao")) docTypeDisplay = "Suspensão Disciplinar";
                            else if (lower.startsWith("ferias")) docTypeDisplay = "Aviso de Férias";

                            return (
                              <tr key={filename} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4">
                                  <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 font-bold text-blue-600 hover:underline text-xs"
                                  >
                                    <FileText size={14} className="shrink-0" />
                                    <span>{filename}</span>
                                  </a>
                                </td>
                                <td className="px-6 py-4 font-semibold text-slate-500">
                                  {docTypeDisplay}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveDocHistory(filename)}
                                    className="p-1.5 text-slate-450 hover:text-rose-650 hover:bg-rose-50 rounded-lg transition-colors text-slate-400"
                                    title="Excluir documento do histórico"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                          {(configs.find(c => c.colaborador === selectedColaboradorDetail)?.arquivos || []).length === 0 && (
                            <tr>
                              <td colSpan="3" className="px-6 py-10 text-center text-slate-400 font-medium italic">
                                Nenhum documento administrativo gerado ou anexado para este colaborador.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* VISÃO ADMIN: 2. IMPORTAÇÃO DE PONTO */}
          {/* ======================================================== */}
          {isAdmin && activeSubTab === "importacao" && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div>
                <h3 className="font-black text-slate-800 text-sm flex items-center gap-2">
                  <Upload className="text-blue-500" /> Importador de Arquivos de Ponto
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Selecione o arquivo Excel (`.xls` ou `.xlsx`) gerado pelo relógio ponto. O sistema analisará a folha e fará a detecção automática.
                </p>
              </div>

              {!parsedData && !importLoading && (
                <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-10 text-center flex flex-col items-center justify-center transition-all bg-slate-50/50 cursor-pointer relative">
                  <FileSpreadsheet className="text-slate-400 h-12 w-12 mb-3" />
                  <span className="font-bold text-xs text-slate-700 uppercase tracking-wider mb-1">Escolher Planilha Excel</span>
                  <span className="text-[10px] text-slate-400">Arraste ou clique para selecionar o arquivo</span>
                  <input
                    type="file"
                    accept=".xls,.xlsx"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {importLoading && (
                <div className="flex flex-col items-center justify-center py-10 space-y-2">
                  <Clock className="animate-spin text-blue-500" size={30} />
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Processando arquivo...</span>
                </div>
              )}

              {parsedData && (
                <div className="space-y-6 animate-fade-in">
                  <div className="flex justify-between items-center bg-blue-50 p-4 rounded-xl border border-blue-100">
                    <div>
                      <p className="text-xs font-bold text-blue-800">
                        Planilha Analisada com Sucesso!
                      </p>
                      <p className="text-[10px] text-blue-600 mt-0.5 font-mono">
                        Período detectado: {parsedData.month}/{parsedData.year} | Encontrados {parsedData.employees.length} funcionários no arquivo.
                      </p>
                    </div>
                    <button
                      onClick={() => setParsedData(null)}
                      className="px-3 py-1.5 bg-white border border-blue-200 text-blue-800 font-bold rounded-lg text-[10px]"
                    >
                      Limpar
                    </button>
                  </div>

                  {/* Configuração de mapeamentos */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-slate-700 text-xs uppercase tracking-wider">Mapeamento de Funcionários:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parsedData.employees.map(emp => {
                        const isMapped = !!importMappings[emp.id];
                        return (
                          <div key={emp.id} className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-bold text-xs text-slate-800 truncate">{emp.name}</p>
                              <p className="text-[9px] text-slate-400 font-mono">ID Relógio: #{emp.id} {emp.dept ? `| Dept: ${emp.dept}` : ""}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                className={`p-1.5 rounded-lg border text-[11px] font-bold bg-white max-w-[180px] outline-none ${
                                  isMapped ? "border-emerald-300 text-emerald-800 bg-emerald-50/20" : "border-amber-300 text-amber-800 bg-amber-50/20"
                                }`}
                                value={importMappings[emp.id] || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setImportMappings(prev => ({ ...prev, [emp.id]: val }));
                                }}
                              >
                                <option value="">-- Não Mapeado --</option>
                                {users.map(u => (
                                  <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Confirmação */}
                  <div className="pt-4 border-t flex justify-end">
                    <button
                      onClick={handleConfirmImport}
                      className="px-6 py-2.5 bg-green-600 text-white font-bold rounded-xl text-xs uppercase tracking-wider hover:bg-green-700 flex items-center gap-2 shadow-lg shadow-green-100"
                    >
                      <CheckCircle size={16} /> Confirmar Importação de Pontos
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* VISÃO ADMIN: 3. ESCALAS DE SÁBADO */}
          {/* ======================================================== */}
          {isAdmin && activeSubTab === "escalas" && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="font-black text-slate-800 text-sm flex items-center gap-2">
                    <CalendarPlus className="text-blue-500" /> Escalas de Sábado (Plantões)
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Crie e visualize plantões de sábados. Funcionários escalados obedecem à jornada do plantão; outros dias contam 100% hora extra.
                  </p>
                </div>
                <button
                  onClick={handleOpenEscala}
                  className="px-4 py-2 bg-blue-600 text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-blue-700 shadow-md shadow-blue-100 flex items-center gap-2"
                >
                  <Plus size={16} /> Criar Escala
                </button>
              </div>

              {/* Tabela de Escalas */}
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                      <th className="px-6 py-3">Sábado</th>
                      <th className="px-6 py-3">Jornada Plantão</th>
                      <th className="px-6 py-3">Colaboradores Escalados</th>
                      <th className="px-6 py-3">Observações</th>
                      <th className="px-6 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {escalas
                      .filter(e => {
                        const dateParts = e.data.split("-");
                        return Number(dateParts[1]) === selectedMonth && Number(dateParts[0]) === selectedYear;
                      })
                      .map(e => (
                        <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-bold text-slate-800">
                            {e.data.split("-").reverse().join("/")} ({getDayOfWeekLabel(e.data)})
                          </td>
                          <td className="px-6 py-4 font-mono font-semibold text-slate-700">
                            {e.jornada_entrada} às {e.jornada_saida} ({
                              minutesToTime(timeToMinutes(e.jornada_saida) - timeToMinutes(e.jornada_entrada))
                            }h)
                          </td>
                          <td className="px-6 py-4 font-medium text-slate-600 max-w-xs">
                            <div className="flex flex-wrap gap-1">
                              {getColaboradoresArray(e.colaboradores).map(cid => {
                                const name = users.find(x => x.id === cid)?.name || "N/A";
                                const uCfg = configs.find(c => c.colaborador === cid);
                                const eqId = typeof uCfg?.equipe === "object" ? uCfg?.equipe?.id : uCfg?.equipe;
                                const eqName = equipes.find(eq => eq.id === eqId)?.nome;
                                return (
                                  <span
                                    key={cid}
                                    className="bg-blue-50 text-blue-800 px-2 py-0.5 rounded text-[10px] font-bold border border-blue-100 inline-flex items-center gap-1"
                                    title={eqName ? `Equipe: ${eqName}` : undefined}
                                  >
                                    {name}
                                    {eqName && (
                                      <span className="text-[9px] font-normal text-blue-600/80">({eqName})</span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-500 italic">
                            {e.observacao || "-"}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleEditEscala(e)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-block"
                                title="Editar escala de plantão"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteEscala(e.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors inline-block"
                                title="Remover plantão"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    {escalas.filter(e => {
                      const dateParts = e.data.split("-");
                      return Number(dateParts[1]) === selectedMonth && Number(dateParts[0]) === selectedYear;
                    }).length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium">
                          Nenhuma escala de sábado programada para este mês.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* VISÃO ADMIN: 4. CONFIGURAÇÕES & CONTRATOS */}
          {/* ======================================================== */}
          {isAdmin && activeSubTab === "configs" && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="font-black text-slate-800 text-sm flex items-center gap-2">
                    <Settings className="text-blue-500" /> Jornadas e Parâmetros dos Colaboradores
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Defina tolerâncias de atraso, saldo de banco de horas inicial e a jornada semanal obrigatória por funcionário.
                  </p>
                </div>
                <button
                  onClick={handleOpenPayment}
                  className="px-4 py-2 bg-emerald-600 text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-emerald-700 shadow-md shadow-emerald-100 flex items-center gap-2"
                >
                  <DollarSign size={16} /> Lançar Bônus/Abono pago
                </button>
              </div>

              {/* Tabela de Contratos e Parametrizações */}
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                      <th className="px-6 py-3">Colaborador</th>
                      <th className="px-4 py-3">Admissão</th>
                      <th className="px-6 py-3">Mapeamento Nome Relógio</th>
                      <th className="px-4 py-3 text-center">Tolerância Diária</th>
                      <th className="px-4 py-3 text-center">Banco Inicial</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-6 py-3 text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => {
                      const config = configs.find(c => c.colaborador === u.id);
                      return (
                        <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-bold text-slate-800">{u.name}</p>
                              <p className="text-[10px] text-slate-400 font-mono">@{u.username}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-600">
                            {config?.data_admissao ? new Date(config.data_admissao).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-6 py-4 font-mono text-[10px] text-slate-500 max-w-xs truncate">
                            {config?.relogio_nomes?.length ? config.relogio_nomes.join(", ") : "-"}
                          </td>
                          <td className="px-4 py-4 text-center font-mono font-semibold text-slate-700">
                            {config?.tolerancia_minutos ?? 10} min
                          </td>
                          <td className="px-4 py-4 text-center font-mono font-semibold text-slate-700">
                            {minutesToTime(config?.banco_horas_inicial ?? 0)}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {config?.ativo !== false ? (
                              <span className="bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded text-[9px] font-bold border border-emerald-100">Ativo</span>
                            ) : (
                              <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[9px] font-bold border border-slate-200">Desativado</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleToggleAtivo(u.id, config)}
                                className={`px-3 py-1.5 rounded-lg font-bold transition-all text-[10px] ${
                                  config?.ativo !== false
                                    ? "bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white"
                                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white"
                                }`}
                              >
                                {config?.ativo !== false ? "Inativar" : "Ativar"}
                              </button>
                              <button
                                onClick={() => handleOpenConfig(u.id)}
                                className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg font-bold transition-all text-[10px]"
                              >
                                Configurar Contrato
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* VISÃO ADMIN: 5. CARGOS & EQUIPES */}
          {/* ======================================================== */}
          {isAdmin && activeSubTab === "cargos_equipes" && (
            <div className="space-y-6 animate-fade-in text-left">
              {/* Top Bar with Company Filter */}
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="font-black text-slate-800 text-sm flex items-center gap-2">
                    <Users className="text-blue-500" /> Estrutura Organizacional: Cargos &amp; Equipes
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Gerencie os cargos (funções, CBO, setores) e as equipes (líderes, vice-líderes, integrantes) de cada empresa.
                  </p>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-xs font-bold text-slate-550 uppercase tracking-wider whitespace-nowrap">Empresa:</label>
                  <select
                    className="p-2.5 border border-slate-350 bg-white rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                    value={selectedCompanyFilter}
                    onChange={(e) => setSelectedCompanyFilter(e.target.value)}
                  >
                    <option value="">Selecione uma empresa...</option>
                    {fiscalConfigs.map(fc => (
                      <option key={fc.id} value={fc.razao_social}>
                        {fc.razao_social}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedCompanyFilter ? (
                <>
                  {/* Navigation tabs inside the sub-tab */}
                  <div className="flex border-b border-slate-200 gap-6">
                    <button
                      onClick={() => setStructureActiveSubTab("cargos")}
                      className={`pb-3 font-bold text-xs uppercase tracking-wider transition-all border-b-2 ${
                        structureActiveSubTab === "cargos"
                          ? "border-blue-600 text-blue-600 font-black"
                          : "border-transparent text-slate-500 hover:text-slate-750"
                      }`}
                    >
                      Cargos / Funções ({cargos.filter(c => c.contratante === selectedCompanyFilter).length})
                    </button>
                    <button
                      onClick={() => setStructureActiveSubTab("equipes")}
                      className={`pb-3 font-bold text-xs uppercase tracking-wider transition-all border-b-2 ${
                        structureActiveSubTab === "equipes"
                          ? "border-blue-600 text-blue-600 font-black"
                          : "border-transparent text-slate-500 hover:text-slate-750"
                      }`}
                    >
                      Equipes &amp; Hierarquia ({equipes.filter(e => e.contratante === selectedCompanyFilter).length})
                    </button>
                  </div>

                  {/* Section 1: CARGOS */}
                  {structureActiveSubTab === "cargos" && (
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Cargos Cadastrados</h4>
                          <p className="text-[11px] text-slate-405 font-medium">Lista de cargos configurados para a empresa {selectedCompanyFilter}.</p>
                        </div>
                        <button
                          onClick={() => handleOpenCargo(null)}
                          className="px-4 py-2 bg-blue-600 text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-blue-700 shadow-md shadow-blue-100 flex items-center gap-2"
                        >
                          <Plus size={16} /> Adicionar Cargo
                        </button>
                      </div>

                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                              <th className="px-6 py-3">Nome do Cargo</th>
                              <th className="px-6 py-3">Código CBO</th>
                              <th className="px-6 py-3">Setor</th>
                              <th className="px-6 py-3">Descrição</th>
                              <th className="px-6 py-3 text-right">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cargos
                              .filter(c => c.contratante === selectedCompanyFilter)
                              .map(c => (
                                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                  <td className="px-6 py-4 font-bold text-slate-800">{c.nome}</td>
                                  <td className="px-6 py-4 font-mono font-semibold text-slate-700">{c.cbo || "-"}</td>
                                  <td className="px-6 py-4">
                                    <span className="bg-slate-100 text-slate-750 px-2 py-0.5 rounded text-[10px] font-bold border border-slate-200">
                                      {c.setor || "Geral"}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-slate-500 max-w-xs truncate" title={c.descricao}>
                                    {c.descricao || "-"}
                                  </td>
                                  <td className="px-6 py-4 text-right flex justify-end gap-2">
                                    <button
                                      onClick={() => handleOpenCargo(c)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Editar cargo"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCargo(c.id)}
                                      className="p-1.5 text-red-655 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Excluir cargo"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            {cargos.filter(c => c.contratante === selectedCompanyFilter).length === 0 && (
                              <tr>
                                <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium">
                                  Nenhum cargo cadastrado para esta empresa.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Section 2: EQUIPES */}
                  {structureActiveSubTab === "equipes" && (
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Equipes &amp; Integrantes</h4>
                          <p className="text-[11px] text-slate-405 font-medium">Gerencie equipes, atribua líderes e veja os colaboradores que pertencem a cada time.</p>
                        </div>
                        <button
                          onClick={() => handleOpenEquipe(null)}
                          className="px-4 py-2 bg-blue-600 text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-blue-700 shadow-md shadow-blue-100 flex items-center gap-2"
                        >
                          <Plus size={16} /> Adicionar Equipe
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {equipes
                          .filter(eq => eq.contratante === selectedCompanyFilter)
                          .map(eq => {
                            const leaderUser = users.find(u => u.id === eq.lider);
                            const viceUser = users.find(u => u.id === eq.vice_lider);
                            const teamMembers = configs.filter(c => c.equipe === eq.id && c.ativo !== false);
                            
                            return (
                              <div key={eq.id} className="bg-slate-50/50 rounded-2xl border border-slate-200 p-5 space-y-4 hover:shadow-md transition-shadow relative">
                                <div className="flex justify-between items-start gap-4">
                                  <div>
                                    <h5 className="font-black text-slate-800 text-sm">{eq.nome}</h5>
                                    <p className="text-[10px] text-slate-450 italic mt-0.5">{eq.descricao || "Sem descrição"}</p>
                                  </div>
                                  <div className="flex gap-1.5">
                                    <button
                                      onClick={() => handleOpenEquipe(eq)}
                                      className="p-1.5 text-blue-600 hover:bg-white rounded-lg border border-slate-200 transition-colors bg-white shadow-sm"
                                      title="Editar equipe"
                                    >
                                      <Edit2 size={13} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteEquipe(eq.id)}
                                      className="p-1.5 text-red-655 hover:bg-white rounded-lg border border-slate-200 transition-colors bg-white shadow-sm"
                                      title="Excluir equipe"
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-xs border-t border-b border-slate-150 py-3">
                                  <div>
                                    <span className="text-slate-400 block text-[9px] uppercase tracking-wider font-extrabold">Líder</span>
                                    <span className="font-bold text-slate-700">
                                      {leaderUser ? leaderUser.name : <span className="text-slate-400 font-normal italic">Não definido</span>}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block text-[9px] uppercase tracking-wider font-extrabold">Vice-Líder</span>
                                    <span className="font-bold text-slate-700">
                                      {viceUser ? viceUser.name : <span className="text-slate-400 font-normal italic">Não definido</span>}
                                    </span>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <span className="text-slate-400 block text-[9px] uppercase tracking-wider font-extrabold">Integrantes ({teamMembers.length})</span>
                                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                                    {teamMembers.map(m => {
                                      const memberUser = users.find(u => u.id === m.colaborador);
                                      const memberCargo = cargos.find(c => c.id === m.cargo);
                                      if (!memberUser) return null;
                                      const isLeader = memberUser.id === eq.lider;
                                      const isVice = memberUser.id === eq.vice_lider;

                                      return (
                                        <div 
                                          key={m.id} 
                                          onClick={() => handleOpenColaboradorPanel(memberUser.id, "ficha")}
                                          className="bg-white p-2.5 rounded-xl border border-slate-150 flex items-center justify-between text-xs font-semibold hover:border-blue-300 transition-colors cursor-pointer"
                                        >
                                          <div className="min-w-0 flex-1">
                                            <span className="text-slate-800 font-bold block truncate">{memberUser.name}</span>
                                            <span className="text-[10px] text-slate-450 block truncate">
                                              {memberCargo ? memberCargo.nome : "Sem cargo cadastrado"}
                                            </span>
                                          </div>
                                          <div className="flex gap-1 shrink-0 ml-3">
                                            {isLeader && (
                                              <span className="bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider">
                                                Líder
                                              </span>
                                            )}
                                            {isVice && (
                                              <span className="bg-blue-50 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider">
                                                Vice
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {teamMembers.length === 0 && (
                                      <p className="text-[11px] text-slate-400 italic py-2 text-center bg-white rounded-xl border border-dashed border-slate-200">
                                        Nenhum colaborador alocado nesta equipe.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        {equipes.filter(eq => eq.contratante === selectedCompanyFilter).length === 0 && (
                          <div className="col-span-2 py-12 text-center text-slate-400 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            Nenhuma equipe cadastrada para esta empresa.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white p-12 text-center rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center space-y-3">
                  <div className="h-12 w-12 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center border">
                    <Users size={24} />
                  </div>
                  <h4 className="font-extrabold text-slate-700 text-sm">Empresa Não Selecionada</h4>
                  <p className="text-xs text-slate-400 max-w-sm">
                    Selecione uma Empresa Contratante no filtro superior para gerenciar os cargos e equipes correspondentes.
                  </p>
                </div>
              )}

              {/* MODALS */}
              {isCargoModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
                  <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in">
                    <div className="p-5 border-b flex justify-between items-center bg-slate-50">
                      <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                        <Users className="text-blue-600" /> {cargoEditId ? "Editar Cargo" : "Novo Cargo"}
                      </h4>
                      <button onClick={() => setIsCargoModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleSaveCargo} className="p-6 space-y-4 text-left">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Cargo *</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ex: Auxiliar de Almoxarifado"
                          value={cargoNome}
                          onChange={(e) => setCargoNome(e.target.value)}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Código CBO</label>
                          <input
                            type="text"
                            className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ex: 4110-05"
                            value={cargoCBO}
                            onChange={(e) => setCargoCBO(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Setor</label>
                          <input
                            type="text"
                            className="w-full p-2.5 border border-slate-350 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ex: Logística"
                            value={cargoSetor}
                            onChange={(e) => setCargoSetor(e.target.value)}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição</label>
                        <textarea
                          rows="3"
                          className="w-full p-2.5 border border-slate-350 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 resize-none font-semibold"
                          placeholder="Descreva as principais atribuições e responsabilidades deste cargo..."
                          value={cargoDesc}
                          onChange={(e) => setCargoDesc(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Empresa Contratante</label>
                        <select
                          className="w-full p-2.5 border border-slate-350 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={cargoContratante}
                          onChange={(e) => setCargoContratante(e.target.value)}
                          required
                        >
                          {fiscalConfigs.map(fc => (
                            <option key={fc.id} value={fc.razao_social}>
                              {fc.razao_social}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                          type="button"
                          onClick={() => setIsCargoModalOpen(false)}
                          className="px-5 py-2 border rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 shadow-md shadow-blue-100"
                        >
                          Salvar Cargo
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {isEquipeModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
                  <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in">
                    <div className="p-5 border-b flex justify-between items-center bg-slate-50">
                      <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                        <Users className="text-blue-600" /> {equipeEditId ? "Editar Equipe" : "Nova Equipe"}
                      </h4>
                      <button onClick={() => setIsEquipeModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleSaveEquipe} className="p-6 space-y-4 text-left">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome da Equipe *</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-355 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ex: Time Financeiro"
                          value={equipeNome}
                          onChange={(e) => setEquipeNome(e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição</label>
                        <textarea
                          rows="2"
                          className="w-full p-2.5 border border-slate-355 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 resize-none font-semibold"
                          placeholder="Ex: Responsável por faturamento, contas a pagar e receber..."
                          value={equipeDesc}
                          onChange={(e) => setEquipeDesc(e.target.value)}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Líder da Equipe</label>
                          <select
                            className="w-full p-2.5 border border-slate-355 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                            value={equipeLider}
                            onChange={(e) => setEquipeLider(e.target.value)}
                          >
                            <option value="">Selecione um líder...</option>
                            {users.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Vice-Líder</label>
                          <select
                            className="w-full p-2.5 border border-slate-355 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                            value={equipeViceLider}
                            onChange={(e) => setEquipeViceLider(e.target.value)}
                          >
                            <option value="">Selecione um vice...</option>
                            {users.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Empresa Contratante</label>
                        <select
                          className="w-full p-2.5 border border-slate-355 bg-white rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold text-slate-700"
                          value={equipeContratante}
                          onChange={(e) => setEquipeContratante(e.target.value)}
                          required
                        >
                          {fiscalConfigs.map(fc => (
                            <option key={fc.id} value={fc.razao_social}>
                              {fc.razao_social}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                          type="button"
                          onClick={() => setIsEquipeModalOpen(false)}
                          className="px-5 py-2 border rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 shadow-md shadow-blue-100"
                        >
                          Salvar Equipe
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* UNIFIED COLLABORATOR DASHBOARD */}
          {/* ======================================================== */}
          {!isAdmin && (
            <div className="space-y-6 animate-fade-in">
              {/* Header do Espelho */}
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h3 className="font-black text-slate-800 text-lg flex flex-wrap items-center gap-3">
                    <span>Painel do Colaborador: {user?.name}</span>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold border shadow-sm transition-all ${
                      saldoAcumulado6Meses >= 0 
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                        : "bg-rose-50 text-rose-700 border-rose-200"
                    }`}>
                      <TrendingUp size={12} className={saldoAcumulado6Meses >= 0 ? "" : "rotate-180"} />
                      Acumulado 6 Meses: {saldoAcumulado6Meses >= 0 ? "+" : ""}{minutesToTime(saldoAcumulado6Meses)}
                    </span>
                  </h3>
                  <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-0.5">
                    Visualização consolidada de ponto, banco de horas e escalas
                  </p>
                </div>
                <div className="bg-blue-50 text-blue-800 px-3 py-1.5 rounded-xl border border-blue-100 font-bold text-xs">
                  Acesso: Colaborador (Apenas Leitura)
                </div>
              </div>

              {/* Grid de Métricas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Card 1: Saldo Acumulado */}
                {(() => {
                  const m = getColaboradorMonthlyMetrics(user?.id);
                  const isPositive = m.accumulatedTotal >= 0;
                  return (
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 font-bold ${
                        isPositive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                      }`}>
                        <Clock size={20} />
                      </div>
                      <div>
                        <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Banco de Horas Acumulado</h4>
                        <p className={`text-lg font-black ${isPositive ? "text-emerald-600" : "text-rose-600"}`}>
                          {minutesToTime(m.accumulatedTotal)}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Card 2: Saldo Período */}
                {(() => {
                  const m = getColaboradorMonthlyMetrics(user?.id);
                  const isPositive = m.periodBalance >= 0;
                  return (
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 font-bold ${
                        isPositive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                      }`}>
                        <TrendingUp size={20} className={isPositive ? "" : "rotate-180"} />
                      </div>
                      <div>
                        <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Saldo deste Mês ({selectedMonth}/{selectedYear})</h4>
                        <p className={`text-lg font-black ${isPositive ? "text-emerald-600" : "text-rose-600"}`}>
                          {isPositive ? "+" : ""}{minutesToTime(m.periodBalance)}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Card 3: Próxima Escala */}
                {(() => {
                  const today = new Date().toISOString().split("T")[0];
                  const userEscalas = escalas
                    .filter(e => getColaboradoresArray(e.colaboradores).includes(user?.id) && e.data >= today)
                    .sort((a, b) => a.data.localeCompare(b.data));
                  const nextEscala = userEscalas[0];
                  return (
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Calendar size={20} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">Próxima Escala de Sábado</h4>
                        <p className="text-xs font-black text-slate-800 truncate">
                          {nextEscala 
                            ? `${nextEscala.data.split("-").reverse().join("/")} (${nextEscala.jornada_entrada} - ${nextEscala.jornada_saida})`
                            : "Nenhuma escala agendada"}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Layout Duas Colunas */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Coluna Esquerda: Espelho de Ponto (Read-only) */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                      <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-2">
                        <FileSpreadsheet className="text-blue-600" size={16} /> Meu Espelho de Ponto Diário
                      </h4>
                      <span className="text-[10px] text-slate-400 font-bold">Mês de Apuração Selecionado</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="border-b border-slate-200 text-slate-500 font-bold uppercase bg-slate-50">
                            <th className="px-4 py-3 text-center">Dia</th>
                            <th className="px-4 py-3">Jornada Padrão</th>
                            <th className="px-6 py-3">Batidas Originais</th>
                            <th className="px-6 py-3">Batidas Ajustadas</th>
                            <th className="px-4 py-3 text-center">Abono</th>
                            <th className="px-4 py-3 text-center">Trabalhado</th>
                            <th className="px-4 py-3 text-center">Saldo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {espelhoDetalhado.map(d => {
                            const dateObj = new Date(d.date + "T00:00:00");
                            const isWeekend = dateObj.getDay() === 0 || (dateObj.getDay() === 6 && !d.isScale);
                            return (
                              <tr key={d.date} className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${
                                isWeekend ? "bg-slate-50/40 text-slate-400" : "text-slate-700"
                              }`}>
                                <td className="px-4 py-3 text-center font-bold">
                                  {d.dayNum} ({getDayOfWeekLabel(d.date)})
                                </td>
                                <td className="px-4 py-3 font-medium text-[10px]">
                                  {d.labelJornada}
                                </td>
                                <td className="px-6 py-3 font-mono text-[11px] font-semibold text-slate-500">
                                  {d.original.length ? d.original.join(" ➔ ") : "-"}
                                </td>
                                <td className="px-6 py-3 font-mono text-[11px] font-bold text-slate-800">
                                  {d.adjusted.length ? (
                                    <div className="flex flex-col gap-1 items-start">
                                      <div className="flex items-center gap-1.5">
                                        <span className="bg-yellow-50 text-yellow-800 px-1.5 py-0.5 rounded text-[10px] font-bold border border-yellow-200">Ajustado</span>
                                        <span>{d.adjusted.join(" ➔ ")}</span>
                                      </div>
                                      {d.reason && (
                                        <div className="text-[10px] text-slate-500 font-normal italic leading-tight bg-slate-50 border border-slate-200 rounded px-2 py-1 max-w-[200px] whitespace-normal break-words shadow-sm mt-0.5" title={`Motivo: ${d.reason}`}>
                                          <span className="font-extrabold not-italic text-slate-650 block text-[9px] uppercase tracking-wider mb-0.5">Motivo:</span>
                                          {d.reason}
                                        </div>
                                      )}
                                    </div>
                                  ) : "-"}
                                </td>
                                <td className="px-4 py-3 text-center font-mono font-medium">
                                  {d.abono > 0 ? (
                                    <div className="flex flex-col items-center gap-1">
                                      <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-lg border border-purple-100 font-bold" title={d.justificativa_abono}>
                                        +{minutesToTime(d.abono)}
                                      </span>
                                      {d.justificativa_abono && (
                                        <div className="text-[10px] text-slate-500 font-normal italic leading-tight bg-slate-50 border border-slate-200 rounded px-2 py-1 max-w-[150px] whitespace-normal break-words shadow-sm mt-0.5" title={d.justificativa_abono}>
                                          {d.justificativa_abono}
                                        </div>
                                      )}
                                    </div>
                                  ) : "-"}
                                </td>
                                <td className="px-4 py-3 text-center font-mono font-semibold">
                                  {d.trabalhado > 0 ? minutesToTime(d.trabalhado) : "-"}
                                </td>
                                <td className={`px-4 py-3 text-center font-mono font-bold ${
                                  d.saldo > 0 ? "text-emerald-600" : d.saldo < 0 ? "text-rose-600 font-black" : "text-slate-400"
                                }`}>
                                  {d.saldo > 0 ? "+" : ""}{d.saldo !== 0 ? minutesToTime(d.saldo) : "00:00"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Coluna Direita: Sidebar */}
                <div className="space-y-6">
                  {/* Minhas Próximas Escalas */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                    <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-2 border-b pb-2 border-slate-100">
                      <CalendarPlus size={16} className="text-blue-600" /> Próximas Escalas de Sábado
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                      {(() => {
                        const today = new Date().toISOString().split("T")[0];
                        const list = escalas
                          .filter(e => getColaboradoresArray(e.colaboradores).includes(user?.id) && e.data >= today)
                          .sort((a, b) => a.data.localeCompare(b.data));
                        
                        if (list.length === 0) {
                          return <p className="text-xs text-slate-400 italic py-2">Nenhum plantão agendado para os próximos dias.</p>;
                        }
                        
                        return list.map(e => (
                          <div key={e.id} className="bg-slate-50 p-3 rounded-xl border border-slate-150 flex flex-col gap-1 text-[11px] font-semibold text-slate-650">
                            <span className="font-extrabold text-slate-800 text-xs">
                              {e.data.split("-").reverse().join("/")} ({getDayOfWeekLabel(e.data)})
                            </span>
                            <span>Jornada: {e.jornada_entrada} às {e.jornada_saida}</span>
                            {e.observacao && <span className="text-[10px] text-slate-400 italic mt-0.5">Obs: {e.observacao}</span>}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Lançamentos e Bônus */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                    <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-2 border-b pb-2 border-slate-100">
                      <DollarSign size={16} className="text-emerald-500" /> Pagamentos &amp; Bônus do Mês
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                      {(() => {
                        const list = payments.filter(p => p.colaborador === user?.id && p.mes === selectedMonth && p.ano === selectedYear);
                        if (list.length === 0) {
                          return <p className="text-xs text-slate-400 italic py-2">Nenhum bônus ou desconto lançado neste mês.</p>;
                        }
                        return list.map(p => (
                          <div key={p.id} className="flex justify-between items-center bg-slate-50 p-2.5 rounded-xl border border-slate-150 text-[11px] font-semibold">
                            <div className="min-w-0 flex-1">
                              <span className="block text-slate-800 font-extrabold truncate">{p.descricao}</span>
                              <span className="text-[9px] font-bold text-slate-450 uppercase">
                                {p.tipo === "bonus" ? "Bônus" : p.tipo === "desconto" ? "Desconto" : p.tipo === "salario" ? "Salário" : "Horas Pagas"}
                              </span>
                            </div>
                            <div className="text-right shrink-0 ml-3">
                              <span className={`font-mono font-bold text-xs ${p.tipo === "desconto" ? "text-rose-600" : "text-emerald-600"}`}>
                                {p.tipo === "desconto" ? "-" : "+"} R$ {p.valor.toFixed(2)}
                              </span>
                              <span className="block text-[8px] font-black uppercase text-slate-400 mt-0.5">
                                {p.pago ? "Pago" : "Pendente"}
                              </span>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Gráfico do Banco de Horas */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                    <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-2 border-b pb-2 border-slate-100">
                      <TrendingUp size={16} className="text-blue-600" /> Tendência do Banco de Horas
                    </h4>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis dataKey="dia" stroke="#94A3B8" fontSize={9} fontStyle="normal" />
                          <YAxis stroke="#94A3B8" fontSize={9} />
                          <Tooltip 
                            formatter={(value) => [`${value} h`, 'Saldo']} 
                            labelFormatter={(label) => `Dia ${label}`}
                          />
                          <Line type="monotone" dataKey="saldoAcumulado" stroke="#3B82F6" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ======================================================== */}
      {/* MODAL: AJUSTE MANUAL DE PONTO */}
      {/* ======================================================== */}
      {isAdjustmentModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in">
            <div className="p-5 border-b flex justify-between items-center bg-slate-50">
              <div>
                <h4 className="font-black text-slate-800 text-sm">Ajustar Apuração de Ponto</h4>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">Colaborador: {adjustmentColaborador?.name} | Data: {adjustmentDate}</p>
              </div>
              <button onClick={() => setIsAdjustmentModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveAdjustment} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Batidas do Dia (HH:MM separadas por vírgula)</label>
                <input
                  type="text"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 09:00, 13:00, 14:00, 18:00"
                  value={adjPunches}
                  onChange={(e) => setAdjPunches(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Minutos Abonados (Lançamento de Atestados/Dispensas)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 240 (equivalente a 4 horas)"
                  value={adjAbono}
                  onChange={(e) => setAdjAbono(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Justificativa do Ajuste / Abono</label>
                <textarea
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 h-20 resize-none"
                  placeholder="Ex: Atestado médico de 4 horas entregue, ou Esqueceu de bater o ponto na saída."
                  value={adjReason || adjJustificativaAbono}
                  onChange={(e) => {
                    setAdjReason(e.target.value);
                    setAdjJustificativaAbono(e.target.value);
                  }}
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Certificado Médico / Comprovante (PDF/Imagem)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="text-xs"
                    onChange={(e) => setAdjCertificadoFile(e.target.files[0])}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsAdjustmentModalOpen(false)}
                  className="px-5 py-2 border rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 shadow-md shadow-blue-100"
                >
                  Confirmar Correção
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: CRIAR ESCALA DE SÁBADO */}
      {/* ======================================================== */}
      {isEscalaModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in">
            <div className="p-5 border-b flex justify-between items-center bg-slate-50">
              <div>
                <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                  <CalendarPlus className="text-blue-600" /> {escalaEditId ? "Editar Escala de Sábado" : "Programar Escala de Sábado"}
                </h4>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {escalaEditId ? "Modificando os parâmetros e colaboradores desta escala específica." : "Crie uma escala separada para sua equipe sem sobrescrever escalas de outros líderes."}
                </p>
              </div>
              <button onClick={() => setIsEscalaModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEscala} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data do Sábado</label>
                <input
                  type="date"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  value={escalaDate}
                  onChange={(e) => setEscalaDate(e.target.value)}
                  required
                />
                {escalaDate && (() => {
                  const otherEscalas = escalas.filter(e => e.data === escalaDate && e.id !== escalaEditId);
                  if (otherEscalas.length === 0) return null;
                  const otherUsersCount = otherEscalas.reduce((acc, curr) => acc + getColaboradoresArray(curr.colaboradores).length, 0);
                  return (
                    <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-800 flex items-start gap-2">
                      <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold">Informação:</span> Já existe{otherEscalas.length > 1 ? "m" : ""} {otherEscalas.length} outra{otherEscalas.length > 1 ? "s" : ""} escala{otherEscalas.length > 1 ? "s" : ""} neste mesmo sábado ({otherUsersCount} colaborador{otherUsersCount > 1 ? "es" : ""}). Esta escala será gravada como um registro independente, sem apagar a{otherEscalas.length > 1 ? "s" : ""} existente{otherEscalas.length > 1 ? "s" : ""}.
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Horário Entrada</label>
                  <input
                    type="text"
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 text-center"
                    value={escalaEntrada}
                    onChange={(e) => setEscalaEntrada(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Horário Saída</label>
                  <input
                    type="text"
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 text-center"
                    value={escalaSaida}
                    onChange={(e) => setEscalaSaida(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Colaboradores Escalados ({escalaUsers.length} selecionado{escalaUsers.length === 1 ? "" : "s"})
                  </label>
                  {equipes.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 font-semibold">Equipe:</span>
                      <select
                        value={escalaSelectedEquipe}
                        onChange={(e) => setEscalaSelectedEquipe(e.target.value)}
                        className="text-[10px] py-1 px-2 border border-slate-200 rounded-lg outline-none bg-white font-medium text-slate-700"
                      >
                        <option value="">Todas as Equipes</option>
                        {equipes.map(eq => (
                          <option key={eq.id} value={eq.id}>{eq.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {escalaSelectedEquipe && (
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        const teamMemberIds = users
                          .filter(u => {
                            const cfg = configs.find(c => c.colaborador === u.id);
                            const eqId = typeof cfg?.equipe === "object" ? cfg?.equipe?.id : cfg?.equipe;
                            return eqId === escalaSelectedEquipe;
                          })
                          .map(u => u.id);
                        setEscalaUsers(prev => Array.from(new Set([...prev, ...teamMemberIds])));
                      }}
                      className="text-[10px] font-bold text-blue-600 hover:text-blue-800 underline"
                    >
                      + Marcar todos da equipe
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const teamMemberIds = users
                          .filter(u => {
                            const cfg = configs.find(c => c.colaborador === u.id);
                            const eqId = typeof cfg?.equipe === "object" ? cfg?.equipe?.id : cfg?.equipe;
                            return eqId === escalaSelectedEquipe;
                          })
                          .map(u => u.id);
                        setEscalaUsers(prev => prev.filter(id => !teamMemberIds.includes(id)));
                      }}
                      className="text-[10px] font-bold text-slate-500 hover:text-slate-700 underline"
                    >
                      - Desmarcar equipe
                    </button>
                  </div>
                )}

                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-2.5 space-y-1.5 bg-slate-50/50">
                  {users.filter(u => {
                    const config = configs.find(c => c.colaborador === u.id);
                    const isActive = config ? config.ativo !== false : true;
                    if (!isActive) return false;
                    if (escalaSelectedEquipe) {
                      const eqId = typeof config?.equipe === "object" ? config?.equipe?.id : config?.equipe;
                      return eqId === escalaSelectedEquipe;
                    }
                    return true;
                  }).map(u => {
                    const isChecked = escalaUsers.includes(u.id);
                    const userConfig = configs.find(c => c.colaborador === u.id);
                    const userEquipeId = typeof userConfig?.equipe === "object" ? userConfig?.equipe?.id : userConfig?.equipe;
                    const userEquipe = equipes.find(eq => eq.id === userEquipeId);
                    const isScheduledElsewhere = escalaDate && escalas.some(other =>
                      other.data === escalaDate &&
                      other.id !== escalaEditId &&
                      getColaboradoresArray(other.colaboradores).includes(u.id)
                    );

                    return (
                      <label
                        key={u.id}
                        className={`flex items-center justify-between p-1.5 rounded-lg cursor-pointer text-xs font-semibold transition-colors ${
                          isChecked ? "bg-blue-50/80 text-blue-900 border border-blue-100" : "hover:bg-slate-100/70 text-slate-700"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              if (isChecked) {
                                setEscalaUsers(prev => prev.filter(id => id !== u.id));
                              } else {
                                setEscalaUsers(prev => [...prev, u.id]);
                              }
                            }}
                          />
                          <span>{u.name}</span>
                          {userEquipe && (
                            <span className="text-[10px] font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                              {userEquipe.nome}
                            </span>
                          )}
                        </div>
                        {isScheduledElsewhere && (
                          <span className="text-[9px] font-bold text-amber-700 bg-amber-100/70 px-1.5 py-0.5 rounded">
                            Já em outra escala
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Observações do Plantão / Identificação da Equipe</label>
                <input
                  type="text"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Equipe Camille / Plantão de Modelagem"
                  value={escalaObs}
                  onChange={(e) => setEscalaObs(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsEscalaModalOpen(false)}
                  className="px-5 py-2 border rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 shadow-md shadow-blue-100"
                >
                  {escalaEditId ? "Atualizar Escala" : "Salvar Escala"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: LANÇAR BÔNUS OU PAGAMENTO DE HORAS */}
      {/* ======================================================== */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in">
            <div className="p-5 border-b flex justify-between items-center bg-slate-50">
              <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                <DollarSign className="text-emerald-600" /> Registrar Pagamento ou Bônus
              </h4>
              <button onClick={() => setIsPaymentModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSavePayment} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Colaborador Destinatário</label>
                <select
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500"
                  value={payColaboradorId}
                  onChange={(e) => setPayColaboradorId(e.target.value)}
                  required
                >
                  <option value="">-- Selecione o Colaborador --</option>
                  {users.filter(u => {
                    const config = configs.find(c => c.colaborador === u.id);
                    return config ? config.ativo !== false : true;
                  }).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Mês de Apuração</label>
                  <select
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white outline-none"
                    value={payMonth}
                    onChange={(e) => setPayMonth(Number(e.target.value))}
                  >
                    <option value="1">Janeiro</option>
                    <option value="2">Fevereiro</option>
                    <option value="3">Março</option>
                    <option value="4">Abril</option>
                    <option value="5">Maio</option>
                    <option value="6">Junho</option>
                    <option value="7">Julho</option>
                    <option value="8">Agosto</option>
                    <option value="9">Setembro</option>
                    <option value="10">Outubro</option>
                    <option value="11">Novembro</option>
                    <option value="12">Dezembro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Ano de Apuração</label>
                  <select
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white outline-none"
                    value={payYear}
                    onChange={(e) => setPayYear(Number(e.target.value))}
                  >
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                    <option value="2027">2027</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tipo de Lançamento</label>
                  <select
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white outline-none"
                    value={payTipo}
                    onChange={(e) => {
                      setPayTipo(e.target.value);
                      setPayCompensateHours("");
                      setPayCompensateHoursPart(0);
                      setPayCompensateMinutesPart(0);
                      setPayIsDescontoBanco(false);
                    }}
                  >
                    <option value="bonus">Bônus de Desempenho / Pontualidade</option>
                    <option value="banco_horas_pago">Banco de Horas Pago (Dinheiro)</option>
                    <option value="salario">Salário Regular</option>
                    <option value="desconto">Desconto Administrativo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Valor em Reais (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 350.00"
                    value={payValor}
                    onChange={(e) => setPayValor(e.target.value)}
                    required
                  />
                </div>
              </div>

              {payTipo === "banco_horas_pago" && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3 text-left">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Quantidade de Horas Pagas *</label>
                  <div className="flex items-center gap-2 max-w-[200px]">
                    <div className="flex-1">
                      <label className="block text-[8px] font-bold text-slate-450 uppercase mb-1 text-center">Horas</label>
                      <input
                        type="number"
                        min="0"
                        className="w-full p-2.5 border border-slate-300 rounded-xl text-center font-mono text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500"
                        value={payCompensateHoursPart}
                        onChange={(e) => setPayCompensateHoursPart(Math.max(0, parseInt(e.target.value) || 0))}
                      />
                    </div>
                    <div className="text-slate-400 font-bold self-end mb-2.5">:</div>
                    <div className="flex-1">
                      <label className="block text-[8px] font-bold text-slate-450 uppercase mb-1 text-center">Minutos</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        className="w-full p-2.5 border border-slate-300 rounded-xl text-center font-mono text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500"
                        value={payCompensateMinutesPart}
                        onChange={(e) => setPayCompensateMinutesPart(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400">
                    Essas horas serão descontadas do saldo acumulado do colaborador.
                  </p>
                  {(payCompensateHoursPart > 0 || payCompensateMinutesPart > 0) && (
                    <p className="text-[10px] font-bold text-amber-600 bg-amber-50 p-2 rounded-lg border border-amber-200">
                      ⚠️ Esse lançamento abaterá {String(payCompensateHoursPart).padStart(2, "0")}:{String(payCompensateMinutesPart).padStart(2, "0")} horas do saldo de banco de horas do colaborador assim que o status for marcado como 'Pago'.
                    </p>
                  )}
                </div>
              )}

              {payTipo === "desconto" && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={payIsDescontoBanco}
                      onChange={(e) => {
                        setPayIsDescontoBanco(e.target.checked);
                        if (!e.target.checked) {
                          setPayCompensateHours("");
                          setPayCompensateHoursPart(0);
                          setPayCompensateMinutesPart(0);
                        }
                      }}
                    />
                    <span>Descontar Horas Devedoras do Banco de Horas</span>
                  </label>

                  {payIsDescontoBanco && (
                    <div className="space-y-3 text-left">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Quantidade de Horas Descontadas *</label>
                      <div className="flex items-center gap-2 max-w-[200px]">
                        <div className="flex-1">
                          <label className="block text-[8px] font-bold text-slate-450 uppercase mb-1 text-center">Horas</label>
                          <input
                            type="number"
                            min="0"
                            className="w-full p-2.5 border border-slate-300 rounded-xl text-center font-mono text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500"
                            value={payCompensateHoursPart}
                            onChange={(e) => setPayCompensateHoursPart(Math.max(0, parseInt(e.target.value) || 0))}
                          />
                        </div>
                        <div className="text-slate-400 font-bold self-end mb-2.5">:</div>
                        <div className="flex-1">
                          <label className="block text-[8px] font-bold text-slate-450 uppercase mb-1 text-center">Minutos</label>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            className="w-full p-2.5 border border-slate-300 rounded-xl text-center font-mono text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500"
                            value={payCompensateMinutesPart}
                            onChange={(e) => setPayCompensateMinutesPart(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-400">
                        Esse desconto irá abater as horas negativas no saldo acumulado do colaborador.
                      </p>
                      {(payCompensateHoursPart > 0 || payCompensateMinutesPart > 0) && (
                        <p className="text-[10px] font-bold text-amber-600 bg-amber-50 p-2 rounded-lg border border-amber-200">
                          ⚠️ Esse lançamento abaterá {String(payCompensateHoursPart).padStart(2, "0")}:{String(payCompensateMinutesPart).padStart(2, "0")} horas do saldo de banco de horas do colaborador assim que o status for marcado como 'Pago'.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição / Justificativa</label>
                <input
                  type="text"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none"
                  placeholder="Ex: Bônus por pontualidade no ponto - Maio 2026"
                  value={payDesc}
                  onChange={(e) => setPayDesc(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={payPago}
                    onChange={(e) => setPayPago(e.target.checked)}
                  />
                  <span>Confirmar pagamento já realizado / quitado</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="px-5 py-2 border rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-emerald-700 shadow-md shadow-emerald-100"
                >
                  Salvar Lançamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Parâmetros de Documentos Administrativos */}
      {activeDocModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in">
            <div className="p-5 border-b flex justify-between items-center bg-slate-50">
              <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                <FileText className="text-blue-600" /> 
                {activeDocModal === "advertencia" && "Emitir Termo de Advertência"}
                {activeDocModal === "suspensao" && "Emitir Termo de Suspensão"}
                {activeDocModal === "ferias" && "Emitir Aviso de Férias"}
                {activeDocModal === "desistencia_vt" && "Emitir Desistência de Vale Transporte"}
              </h4>
              <button onClick={() => setActiveDocModal(null)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleGenerateDocPDF} className="p-6 space-y-4">
              {/* Campos para Advertência e Suspensão */}
              {(activeDocModal === "advertencia" || activeDocModal === "suspensao") && (
                <>
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data da Ocorrência</label>
                      <input
                        type="date"
                        className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        value={docModalData.dataOcorrencia}
                        onChange={(e) => setDocModalData({ ...docModalData, dataOcorrencia: e.target.value })}
                        required
                      />
                    </div>
                    {activeDocModal === "suspensao" && (
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dias de Suspensão</label>
                        <input
                          type="number"
                          min="1"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                          value={docModalData.diasSuspensao}
                          onChange={(e) => setDocModalData({ ...docModalData, diasSuspensao: e.target.value })}
                          required
                        />
                      </div>
                    )}
                  </div>

                  {activeDocModal === "suspensao" && (
                    <div className="grid grid-cols-2 gap-4 text-left">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Início da Suspensão</label>
                        <input
                          type="date"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                          value={docModalData.dataInicioSuspensao}
                          onChange={(e) => setDocModalData({ ...docModalData, dataInicioSuspensao: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data de Retorno</label>
                        <input
                          type="date"
                          className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                          value={docModalData.dataRetornoSuspensao}
                          onChange={(e) => setDocModalData({ ...docModalData, dataRetornoSuspensao: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="text-left">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição Detalhada do Fato</label>
                    <textarea
                      rows="4"
                      className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      placeholder="Descreva detalhadamente o comportamento ou a infração cometida pelo colaborador..."
                      value={docModalData.motivoOcorrencia}
                      onChange={(e) => setDocModalData({ ...docModalData, motivoOcorrencia: e.target.value })}
                      required
                    ></textarea>
                  </div>
                </>
              )}

              {/* Campos para Férias */}
              {activeDocModal === "ferias" && (
                <>
                  <div className="text-left">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Período Aquisitivo</label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      placeholder="Ex: 2025/2026"
                      value={docModalData.periodoAquisitivo}
                      onChange={(e) => setDocModalData({ ...docModalData, periodoAquisitivo: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-left">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Início do Gozo</label>
                      <input
                        type="date"
                        className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        value={docModalData.dataInicio}
                        onChange={(e) => setDocModalData({ ...docModalData, dataInicio: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Fim do Gozo</label>
                      <input
                        type="date"
                        className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        value={docModalData.dataFim}
                        onChange={(e) => setDocModalData({ ...docModalData, dataFim: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Retorno</label>
                      <input
                        type="date"
                        className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        value={docModalData.dataRetorno}
                        onChange={(e) => setDocModalData({ ...docModalData, dataRetorno: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Campos para Desistência de VT */}
              {activeDocModal === "desistencia_vt" && (
                <div className="text-left">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Meio de Transporte Utilizado</label>
                  <input
                    type="text"
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    placeholder="Ex: veículo próprio (carro/moto), carona"
                    value={docModalData.meioLocomocao}
                    onChange={(e) => setDocModalData({ ...docModalData, meioLocomocao: e.target.value })}
                    required
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Justificativa legal para desconsiderar a obrigação do fornecimento do Vale Transporte.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setActiveDocModal(null)}
                  className="px-5 py-2 border rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 shadow-md shadow-blue-100 flex items-center gap-1.5"
                >
                  <FileText size={14} /> Gerar PDF
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
